/* eslint-disable max-len */
import {parseGeoResponseContentPointLonLat} from "./responseContent";
import {parseGeoResponseContentPointListLonLat} from "./responseContent";
import {parseGeoResponseContentPointListListLonLat} from "./responseContent";
import {getGeoJsonPointGeometry} from "./geojson";
import {getGeoJsonMultiPointGeometry} from "./geojson";
import {getGeoJsonLineStringGeometry} from "./geojson";
import {getGeoJsonMultiLineStringGeometry} from "./geojson";
import {getGeoJsonPolygonGeometry} from "./geojson";
import {getGeoJsonMultiPolygonGeometry} from "./geojson";
import {getGeoJsonCollectionGeometry} from "./geojson";
import {Wkt} from "./wkt";

// TODO: Improve typing?
export type QuestionnaireElementTypeGetResponse = (
    (this: any, content: string) => any
);

export type QuestionnaireElementTypeGetWKT = (
    (content: string, contentType: string) => string
);

export type QuestionnaireElementTypeGetGeoJson = (
    (content: string, contentType: string) => {[key: string]: any}
);

export type QuestionnaireElementTypeGetLatLon = (
    (content: string, contentType: string) => {
        lat: number | undefined,
        lon: number | undefined,
    }
);

const GetResponseIdentityFunction: QuestionnaireElementTypeGetResponse = (
    (i: any) => i
);

export type QuestionnaireElementTypePropertyResourceIdType = (
    "asset" | "questionnaireElementGroup"
);

// Enumeration of options to identify multiple choice questions
export enum MultipleChoice {
    // A question is not multiple choice
    None = 0,
    // A question is multiple choice,
    // expecting just one option to be chosen
    One = 1,
    // A question is multiple choice,
    // and any number of options may be chosen.
    Any = 2,
};

export interface QuestionnaireElementTypeTabGroup {
    group: string;
    title: string;
    description: string;
    needsLicense?: string;
}

export interface QuestionnaireElementTypeParallelGroup {
    group: string;
    title: string;
    description: string;
    /**
     * The name of the parallel property intended to store a localized label
     * name for generally describing or contextualizing the parallel setting,
     * especially response options.
     */
    labelPropertyName?: string | null;
    tabGroup: string;
    tabGroups: QuestionnaireElementTypeTabGroup[]; // ?
}

export interface QuestionnaireElementTypePropertyOption {
    allowedTypes: string[];
    valueOption: string;
    valueOptionText: string;
}

export interface QuestionnaireElementTypeOptions {
    /**
     * Unique key for identifying the element type.
     */
    name: string;
    /**
     * Short descriptive English name for use in frontend UI
     */
    title: string;
    /**
     * Longer English description to use as help in frontend UI
     */
    description: string;
    /**
     * Font awesome icon class and name to use in frontend UI.
     * NOTE: This is overridden by components/base-elements/ElementIcon.js
     * for elements with composite icons.
     */
    icon: [string, string];
    /**
     * Hint for frontend to identify map based questions.
     */
    isGeoQuestion?: boolean;
    /**
     * Hint for editor frontend that the element relates to an asset.
     */
    isAsset?: boolean;
    /**
     * Indicates whether this element can contain page jump rules
     */
    canJump?: boolean;
    /**
     * Hint for frontend that element can have a popup associated with it.
     */
    hasPopup?: boolean;
    /**
     * Hint for the frontend that the element is a question.
     * Also used by the backend for validation.
     */
    isQuestion?: boolean;
    /**
     * Indicates whether the element type permits public aggregation of
     * responses. This is intended for use with e.g. the radioButtonPoll
     * element. It is used by the frontend to determine whether responses
     * to a particular question should prompt for or set responses'
     * aggregationConsent flag.
     */
    allowAggregation?: boolean;
    /**
     * Indicates whether using the element requires a certain license type
     */
    needsLicense?: string;
    /**
     * Indicates that this element belongs in the "programmatic" group, these
     * are commonly elements that do not require user interaction to collect 
     * response data or function  
     */
    isProgrammatic?: boolean;
    /**
     * This indicates whether the element is allowed to be used in a pop-up.
     */
    allowedInPopup?: boolean;
    /**
     * When true, this element type is supported and tracked by the server,
     * but the user cannot create it directly using the frontend UI.
     */
    hiddenFromInterface?: boolean;
    /**
     * Indicate questions that might have multiple answers,
     * assigned different "responseNumber" values.
     * Intended for use with questions that might have a popup
     * of additional questions associated with each answer, e.g.
     * each marker added to a map.
     */
    isMultiQuestion?: boolean;
    /**
     * Special flag for the clickLayer question type, which was unfortunately
     * implemented to store part the response content information in a
     * response's responseNumber attribute. The export backend needs to
     * know that responseNumbers are treated differently for this question
     * type.
     */
    abusesResponseNumber?: boolean;
    /**
     * When true, this element type is considered deprecated and should not be
     * cloned or used in new questionnaires.
     */
    isDeprecated?: boolean;
    /**
     * Used by the backend for validation of response content data.
     */
    responseContentType?: string;
    /**
     * Given some map-related response data, get this information
     * in a well-known text format, where applicable.
     * Should be a function accepting a response content string and
     * response content type string, returning a value (probably
     * a string) to be included in places like data export.
     * https://en.wikipedia.org/wiki/Well-known_text_representation_of_geometry
     */
    getWellKnownText?: QuestionnaireElementTypeGetWKT;
    /**
     * Given some map-related response data, get this information
     * in as a GeoJSON geometry object, where applicable.
     * Should be a function accepting a response content string and
     * response content type string, returning a value (probably
     * a json object) to be included in places like data export.
     * https://en.wikipedia.org/wiki/GeoJSON
     */
    getGeoJsonGeometry?: QuestionnaireElementTypeGetGeoJson;
    /**
     * Given some map-related response data, get this information
     * in as an object containing {lat, lon} values,
     * where applicable.
     * Should be a function accepting a response content string and
     * response content type string, returning an object with two
     * attributes representing latitude (lat) and longitude (lon).
     */
    getLatLon?: QuestionnaireElementTypeGetLatLon;
    /**
     * Given some response data, validate this information and get
     * it in an appropriate format for presentation to the user.
     */
    getResponse?: QuestionnaireElementTypeGetResponse;
    /**
     * To be used for questions intended to function as
     * a poll or as comments visible to other respondents.
     */
    canShareResponses?: boolean;
    /**
     * Indicate questions that expect the respondent to choose
     * options from a group, where the response content
     * is an integer representing the option index and the "options"
     * property is a list of strings labeling each option
     * (for MultipleChoice.One) or a list of integers representing
     * the list of selected option indices (MultipleChoice.Any).
     */
    isMultipleChoice?: number;
    /**
     * Used for the range question type. Indicates that
     * the responseContentType is a `list.[something]` which is expected to
     * always contain two elements, i.e. a low value and a high value.
     * The value of this property should be the type of each element of
     * the pair, e.g. `number` if responseContentType is `list.number`.
     */
    isRangeResponse?: string;
    /**
     * The name of the element property intended to store a localized label
     * name for generally describing or contextualizing the element,
     * especially questions.
     */
    labelPropertyName?: string | null;
    /**
     * The name of the element property intended to represent a minimum
     * acceptable numeric value, for questions accepting a numeric response.
     */
    minValuePropertyName?: string | null;
    /**
     * The name of the element property intended to represent a minimum
     * acceptable numeric value, for questions accepting a numeric response.
     */
    maxValuePropertyName?: string | null;
    /**
     * The name of the element property intended to represent the maximum
     * acceptable length of a string response.
     */
    maxStringLengthPropertyName?: string | null;
    /**
     * Some elements, such as geo questions, allow adding multiple responses
     * to the same question which are distinguished by their differing
     * responseNumbers. Where such element types have a property defining
     * the maximum number of answers accepted in the respondent UI, this
     * attribute should identify the name of that property.
     * See also the `isMultiQuestion` element type flag.
     */
    maximumResponsesPropertyName?: string | null;
    /**
     * A list of additional property names where the property values
     * are likely to contain text that could help to identify this element
     * or to distinguish it from others, e.g. in a data export file.
     * 
     * This list should be given in order of preference. Thus for cases where 
     * only one identifier is needed the order determines priority and
     * fallback sequence.
     * 
     * If this property exists, it will be prioritized above the
     * "labelPropertyName" property, and should also include in the list
     * the value of the "labelPropertyName" property
     */
    identifyingTextPropertyNames?: string[];
    /**
     * Indicates which property of the element contains the data-names for
     * options
     */
    optionsPropertyName?: string;
    /**
     * Indicates which properties of the element contains human readable
     * identifying information for the options (label, price) 
     */
    optionDataPropertyNames?: {[key: string]: string};
    /**
     * An array of groupings for properties used by the frontend
     * for dividing the settings into tabs in the UI.
     */
    tabGroups: QuestionnaireElementTypeTabGroup[]; // ?
    /**
     * An array of groupings for properties that have array values
     * and where the order of those arrays should be kept in sync
     * by the frontend.
     */
    parallelGroups?: QuestionnaireElementTypeParallelGroup[];
    /**
     * Specification for a value on the "context" attribute of a response.
     */
    responseContextSpecification?: {[key: string]: any};
    /**
     * List of recognized properties for the element type.
     */
    propertiesList: QuestionnaireElementTypeProperty[];
    /**
     * Index to sort the element types in for example the element library UI.
     */
    sortIndex?: number;
}

export interface QuestionnaireElementTypeProperty {
    /**
     * Unique key to identify this property.
     */
    key: string;
    /**
     * Indicates what type of data the property is expected to store.
     * This affects how the value is validated, serialized, and
     * deserialized in the backend.
     */
    type: string;
    /**
     * Used by the frontend to determine form field type.
     */
    inputType?: string;
    /**
     * Used by frontend to constrain input selection.
     */
    inputAssetTypes?: string[];
    /**
     * Used for grouping parallel properties together. Does not currently
     * affect non-parallel properties.
     */
    group: string;
    /**
     * Hint for the frontend to visually group properties into tabs
     */
    tabGroup: string;
    /**
     * Default value used by frontend.
     */
    default: any;
    /**
     * Wether the property is included upon creation. Only works for parallelGroups props.
     */
    excludedByDefault?: boolean;
    /**
     * Used for properties that have a "list.*" type (e.g. all parallel 
     * properties). When adding a new item to the value, use this value
     * for the insertion if no other value is provided.  
     */
    defaultInsert?: any;
    /**
     * Used when the default value (or default insert) needs to be dynamic
     * e.g. a unique ID, unique color, etc. Currently questionnaire elements
     * support "uid", "palette", "dateNow"
     */
    autoFill?: "dateNow" | "palette" | "uid";
    /**
     * Indicates that this property is used as a data-name (locally unique identifier)
     */
    isDataName?: boolean;
    /**
     * Short descriptive English name for use in frontend UI
     */
    title?: string;
    /**
     * Longer English description to use as help in frontend UI
     */
    description?: string;
    /**
     * Whether it should be considered normal for the property to
     * have localized values for different languages.
     */
    multilingual?: boolean;
    /**
     * This property is only allowed in popups, e.g. click layer related data keys,
     */
    onlyAllowedInPopups?: boolean;
    /**
     * This property is not allowed in popups, e.g. page jump related settings, since
     * preferred selection can not be decided
     */
    notAllowedInPopups?: boolean;
    /**
     * A set of options to choose the property value from.
     */
    valueOptions?: any[];
    /**
     * A set of strings to use for the value selection UI.
     */
    valueOptionTexts?: string[];
    /**
     * A set of icons to use for the value selection UI.
     * Takes precedence over "valueOptionTexts".
     */
    valueOptionIcons?: [string, string][] | string[];
    /**
     * When true, this property is supported and tracked by the server,
     * but the user cannot edit it directly using the frontend UI.
     */
    hiddenFromInterface?: boolean;
    /**
     * Indicates whether the value of the property is meant to represent
     * a resource ID.
     */
    isResourceId?: QuestionnaireElementTypePropertyResourceIdType;
    /**
     * Indicates whether the value of the property is meant to represent
     * a list of resource IDs.
     */
    isResourceIdList?: QuestionnaireElementTypePropertyResourceIdType;
    /**
     * Units to use with properties that have numeric values.
     */

    unit?: string;
    /**
     * Used by the frontend to determine whether to show this property 
     * setting. "key" identifies a different "adjacent" property. If 
     * that property has the value specified by "value" here, then this
     * property's setting is shown in the frontend.
     */
    showIfProperty?: {key: string; value: (string[] | boolean[])};
    /**
     * Used to indicate the number format when inputType": "slider" is 
     * used to set the property value
     */
    format?: string;
    /**
     * Used to indicate the minimum value when "inputType": "integer"/"number" 
     * is used to set the property value
     */
    min?: number;
    /**
     * Used to indicate the maximum value when "inputType": "integer"/"number" 
     * is used to set the property value
     */
    max?: number;
    /**
     * Used to indicate the step size when inputType": "slider" is 
     * used to set the property value
     */
    step?: number;
    /**
     * Used to indicate the slider limits when inputType": "slider" is 
     * used to set the property value
     */
    limits?: [number, number];
    /**
     * Used to define the ticks when inputType": "slider" is 
     * used to set the property value
     */
    ticks?: number[];
    /**
     * Used to indicate the type of assets to allow when 
     * "inputType": "assetPicker" is being used to set the property
     * value
     */
    assetGroup?: string;
    /**
     * Native attribute for the inputType element.
     */
    "native.rows"?: number;
    /**
     * When true, this property is considered isDeprecated and should not be
     * cloned or used in new questionnaires.
     */
    isDeprecated?: boolean;
    /**
     * Indicates whether using the element requires a certain license type
     */
    needsLicense?: string;
}

export class QuestionnaireElementType {
    static readonly list: QuestionnaireElementType[] = [];
    static readonly nameList: string[] = [];
    static readonly map: {[name: string]: QuestionnaireElementType} = {};
    static MultipleChoice = MultipleChoice;
    
    options: QuestionnaireElementTypeOptions;
    name: string;
    title: string;
    description: string;
    icon: [string, string];
    isGeoQuestion: boolean;
    isAsset: boolean;
    canJump: boolean;
    hasPopup: boolean;
    isQuestion: boolean;
    sortIndex?: number;
    allowAggregation: boolean;
    needsLicense: string;
    isProgrammatic: boolean;
    allowedInPopup: boolean;
    hiddenFromInterface: boolean;
    isMultiQuestion: boolean;
    abusesResponseNumber: boolean;
    isDeprecated: boolean;
    responseContentType: string;
    getWellKnownText?: QuestionnaireElementTypeGetWKT;
    getGeoJsonGeometry?: QuestionnaireElementTypeGetGeoJson;
    getLatLon?: QuestionnaireElementTypeGetLatLon;
    getResponse: QuestionnaireElementTypeGetResponse;
    canShareResponses: boolean;
    isMultipleChoice: number;
    isRangeResponse: string;
    labelPropertyName: string;
    minValuePropertyName: string;
    maxValuePropertyName: string;
    maxStringLengthPropertyName: string;
    maximumResponsesPropertyName: string;
    identifyingTextPropertyNames: string[];
    optionsPropertyName: string;
    optionDataPropertyNames: {[key: string]: string};
    tabGroups: QuestionnaireElementTypeTabGroup[]; // ?
    parallelGroups?: QuestionnaireElementTypeParallelGroup[];
    responseContextSpecification?: {[key: string]: any};
    propertiesList: QuestionnaireElementTypeProperty[];
    properties: {[key: string]: QuestionnaireElementTypeProperty};
    
    constructor(options: QuestionnaireElementTypeOptions) {
        this.options = options;
        this.name = options.name;
        this.title = options.title;
        this.description = options.description;
        this.icon = options.icon;
        this.isGeoQuestion = !!options.isGeoQuestion;
        this.isAsset = !!options.isAsset;
        this.canJump = !!options.canJump;
        this.hasPopup = !!options.hasPopup;
        this.isQuestion = !!options.isQuestion;
        this.sortIndex = options.sortIndex || 100;
        this.allowAggregation = !!options.allowAggregation;
        this.needsLicense = options.needsLicense || "";
        this.isProgrammatic = !!options.isProgrammatic;
        this.allowedInPopup = !!options.allowedInPopup;
        this.hiddenFromInterface = !!options.hiddenFromInterface;
        this.isMultiQuestion = !!options.isMultiQuestion;
        this.abusesResponseNumber = !!options.abusesResponseNumber;
        this.isDeprecated = !!options.isDeprecated;
        this.responseContentType = options.responseContentType || "";
        this.getWellKnownText = options.getWellKnownText;
        this.getGeoJsonGeometry = options.getGeoJsonGeometry;
        this.getLatLon = options.getLatLon;
        this.getResponse = options.getResponse || GetResponseIdentityFunction;
        this.canShareResponses = !!options.canShareResponses;
        this.isMultipleChoice = options.isMultipleChoice || MultipleChoice.None;
        this.isRangeResponse = options.isRangeResponse || "";
        this.labelPropertyName = options.labelPropertyName || "";
        this.minValuePropertyName = options.minValuePropertyName || "";
        this.maxValuePropertyName = options.maxValuePropertyName || "";
        this.maxStringLengthPropertyName = options.maxStringLengthPropertyName || "";
        this.maximumResponsesPropertyName = options.maximumResponsesPropertyName || "";
        this.identifyingTextPropertyNames = options.identifyingTextPropertyNames || [];
        this.optionsPropertyName = options.optionsPropertyName || "";
        this.optionDataPropertyNames = options.optionDataPropertyNames || {};
        this.tabGroups = options.tabGroups || [];
        this.parallelGroups = options.parallelGroups || [];
        this.responseContextSpecification = options.responseContextSpecification;
        this.propertiesList = options.propertiesList;
        this.properties = {};
        for(const property of options.propertiesList) {
            this.properties[property.key] = property;
        }
        QuestionnaireElementType.list.push(this);
        QuestionnaireElementType.nameList.push(this.name);
        QuestionnaireElementType.map[this.name] = this;
    }
}

const basicTabGroup: QuestionnaireElementTypeTabGroup = {
    group: "basic",
    title: "Basic settings",
    description: "Primary settings that are needed for this element.",
};
const extraTabGroup: QuestionnaireElementTypeTabGroup = {
    group: "extra",
    title: "Extra settings",
    description: "Additional settings for this element.",
};
const styleTabGroup: QuestionnaireElementTypeTabGroup = {
    group: "style",
    title: "Style settings",
    description: "Additional settings that govern the style of this element.",
};
const responseTabGroup: QuestionnaireElementTypeTabGroup = {
    group: "response",
    title: "Response settings",
    description: "Settings that govern questionnaire's maps and other interactive backgrounds.",
};
const budgetingTabGroup: QuestionnaireElementTypeTabGroup = {
    group: "budgeting",
    title: "Participatory budgeting settings",
    description: "Participatory budgeting settings for this element.",
    needsLicense: "module:participatory-budgeting",
};

const reportOptions: QuestionnaireElementTypePropertyOption[] = [
    {
        allowedTypes: ["textEntry"],
        valueOption: "respondentEmailAddress",
        valueOptionText: "Email address",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentFullName",
        valueOptionText: "Full name",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentFirstName",
        valueOptionText: "First name",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentLastName",
        valueOptionText: "Last name",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentStreetAddress1",
        valueOptionText: "Street address",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentStreetAddress2",
        valueOptionText: "Street address (line 2)",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentRegion",
        valueOptionText: "Region",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentCity",
        valueOptionText: "City",
    }, {
        allowedTypes: ["textEntry"],
        valueOption: "respondentPostCode",
        valueOptionText: "Post code",
    }, {
        allowedTypes: ["radioButtonList"],
        valueOption: "respondentCategory",
        valueOptionText: "Category",
    }
];

const participatoryIdeaOptions: QuestionnaireElementTypePropertyOption[] = [
    {
        allowedTypes: ["textEntry"],
        valueOption: "participatoryIdeaTitle",
        valueOptionText: "Participatory idea title",
    },
    {
        allowedTypes: ["textEntry"],
        valueOption: "participatoryIdeaDescription",
        valueOptionText: "Participatory idea description",
    },
    {
        allowedTypes: ["geoPoint"],
        valueOption: "participatoryIdeaLocation",
        valueOptionText: "Participatory idea location",
    },
    {
        allowedTypes: ["radioButtonList"],
        valueOption: "participatoryIdeaCategory",
        valueOptionText: "Participatory idea category",
    },
];

const ParticipatoryIdeaProperty = (key: string) => (<QuestionnaireElementTypeProperty> {
    "key": "participatoryInfoType",
    "type": "string",
    "inputType": "ownOptionList",
    "valueOptions": participatoryIdeaOptions
        .filter(o => o.allowedTypes.includes(key))
        .map(o => o.valueOption),
    "valueOptionTexts": participatoryIdeaOptions
        .filter(o => o.allowedTypes.includes(key))
        .map(o => o.valueOptionText),
    "group": "functional",
    "tabGroup": "budgeting",
    "default": null,
    "title": "Respondent Information",
    "description": "Use this to indicate that the question is collecting a particular type of respondent information.",
    "needsLicense": "feature:questionnaire:pdf-reporting",
    "multilingual": false,
});

// DEPRECATED - PDF report now uses textEntry with validateEmail property instead
const ReportRespondentProperty = (key: string) => (<QuestionnaireElementTypeProperty> {
    "key": "respondentInfoType",
    "type": "string",
    "inputType": "ownOptionList",
    "valueOptions": reportOptions
        .filter(o => o.allowedTypes.includes(key))
        .map(o => o.valueOption),
    "valueOptionTexts": reportOptions
        .filter(o => o.allowedTypes.includes(key))
        .map(o => o.valueOptionText),
    "group": "functional",
    "tabGroup": "report",
    "default": null,
    "title": "Respondent Information",
    "description": "Use this to indicate that the question is collecting a particular type of respondent information.",
    "needsLicense": "feature:questionnaire:pdf-reporting",
    "multilingual": false,
    "hiddenFromInterface": true,
    "isDeprecated": true,
});

// TODO: textentry for the title 
// and similar thing for allowedtype geopoint,
// which would be the location of the idea

const PublicQuestionElementProperties: QuestionnaireElementTypeProperty[] = [{
    "key": "publicResponses",
    "type": "boolean",
    "inputType": "checkbox",
    "group": "content",
    "tabGroup": "extra",
    "default": false,
    "title": "Make responses public",
    "description": "Responses are publicly shared and visible among respondents",
    "multilingual": false,
    "needsLicense": "feature:questionnaire:response-reactions",
}];

// DEPRECIATED
// DEPRECIATED
// AnalysisProperties depreciated 2024. See PR #2163
const AnalysisProperties: QuestionnaireElementTypeProperty[] = [{
    "key": "analysisChartColorPalette",
    "type": "list.string",
    "group": "hidden",
    "tabGroup": "basic",
    "inputType": "color",
    "default": [],
    "defaultInsert": "",
    "title": "Graph color",
    "description": "The color to use for this option in the response graphs. Use the color selector or specify a hex value.",
    "multilingual": false,
    "hiddenFromInterface": true,
    "isDeprecated": true,
    // "doNotPublish": true, // TODO: Make this work questionnairePublication.js, generateQuestionnaitePublication()
}, {
    "key": "analysisChartType",
    "type": "string",
    "group": "hidden",
    "tabGroup": "basic",
    "inputType": "ownOption",
    "valueOptions": ["pie", "doughnut", "bar", "horizontalBar", "counter"],
    "valueOptionTexts": ["Pie", "Doughnut", "Bar", "Horizontal bar", "Counter"],
    "default": "bar",
    "title": "Results chart type",
    "description": "The type of the chart to use for displaying the aggregate results.",
    "multilingual": false,
    "hiddenFromInterface": true,
    "isDeprecated": true,
    // "doNotPublish": true, // TODO: Make this work questionnairePublication.js, generateQuestionnaitePublication()
}, {
    "key": "analysisChartNumberOfBins",
    "type": "integer",
    "group": "hidden",
    "tabGroup": "basic",
    "inputType": "integer",
    "default": 10,
    "title": "Number of bins",
    "description": "The number of bins for histogram charts",
    "multilingual": false,
    "hiddenFromInterface": true,
    // "doNotPublish": true, // TODO: Make this work questionnairePublication.js, generateQuestionnaitePublication()
    "isDeprecated": true,
}, {
    "key": "analysisChartShowLabels",
    "type": "boolean",
    "group": "hidden",
    "tabGroup": "basic",
    "default": true,
    "title": "Show axis labels",
    "description": "Select to show axis labels",
    "hiddenFromInterface": true,
    "isDeprecated": true,
}, {
    "key": "analysisChartShowDataLabels",
    "type": "boolean",
    "group": "hidden",
    "tabGroup": "basic",
    "default": false,
    "title": "Display values",
    "description": "Select to display individual values in the chart",
    "hiddenFromInterface": true,
    "isDeprecated": true,
}];
// DEPRECIATED
// DEPRECIATED

const ButtonProperties: QuestionnaireElementTypeProperty[] = [{
    "key": "buttonText",
    "type": "string",
    "inputType": "text",
    "group": "content",
    "tabGroup": "basic",
    "default": null,
    "title": "Button text",
    "description": "The text to display on the button.",
    "multilingual": true,
}, {
    "key": "buttonColor",
    "type": "string",
    "inputType": "color",
    "group": "style",
    "tabGroup": "basic",
    // default is null so that the style template color is used when no color is set here
    "default": null,
    "title": "Button color",
    "description": "The color to use for the button. Use the color selector or specify a hex value.",
    "multilingual": false,
}, {
    "key": "darkTextColor",
    "type": "boolean",
    "group": "style",
    "tabGroup": "basic",
    "inputType": "switch",
    "default": false,
    "title": "Dark text color",
    "description": "Use a dark text color for the button text.",
    "multilingual": false,
}, {
    "key": "invertColors",
    "type": "boolean",
    "group": "style",
    "tabGroup": "basic",
    "inputType": "switch",
    "default": false,
    "title": "Invert colors",
    "description": "Switch the button color and the text color.",
    "multilingual": false,
}, {
    "key": "icon",
    "type": "list.string",
    "inputType": "iconOption",
    "group": "style",
    "tabGroup": "basic",
    "default": null,
    "title": "Icon",
    "description": "Icon to show in the button. See <a href='https://fontawesome.com/icons' target='_blank'>Font Awesome</a> for supported icons.",
    "multilingual": false,
}, {
    "key": "iconClass",
    "type": "string",
    "inputType": "iconClassOption",
    "group": "style",
    "tabGroup": "basic",
    "default": "fal",
    "title": "Icon weight",
    "description": "The weight (light, regular, bold) to use for the icon.",
    "multilingual": false,
}];

const GeoProperties: QuestionnaireElementTypeProperty[] = [{
    "key": "minimumResponseZoom",
    "type": "number",
    "inputType": "integer",
    "group": "functional",
    "tabGroup": "extra",
    "min": 0,
    "max": 25,
    "default": null,
    "title": "Minimum zoom level",
    "description": "Respondents must zoom closer than this zoom level to be able to leave a response.",
    "multilingual": false,
}];

const DataUrlProperties: QuestionnaireElementTypeProperty[] = [{
    "key": "dataUrl",
    "type": "url",
    "inputType": "text",
    "group": "functional",
    "tabGroup": "basic",
    "onlyAllowedInPopups": true,
    "default": null,
    "title": "Data key for url",
    "description": "Enter the 'key' or attribute name for fetching the url from the Click Layer's attribute table (shapefile), properties (GeoJSON), or feature info (WFS).",
    "multilingual": true,
}]
// ************** TODO: Font-size for axis labels *********** //
// ************** TODO: x- / y-axis line toggle *********** //

/** ******************************************************************
 *
 * Functional elements
 *
 ********************************************************************/
export const tracker = new QuestionnaireElementType({
    name: "tracker",
    isQuestion: true,
    isProgrammatic: true,
    allowedInPopup: false,
    icon: ["fal", "tag"],
    title: "UTM tracker",
    description: "UTM code tracker",
    responseContentType: "string",
    labelPropertyName: "label",
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "code",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["utm_source", "utm_medium", "utm_content", "utm_campaign", "utm_term"],
        "valueOptionTexts": ["source", "medium", "content", "campaign", "term"],
        "group": "functional",
        "tabGroup": "basic",
        "default": "utm_source",
        "title": "Parameter",
        "description": "Select which UTM parameter you want to track.",
        "multilingual": false,
    }, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Label",
        "description": "Select a label to identify this tracker in the response data.",
        "multilingual": false,
    }],
});

export const panelTracker = new QuestionnaireElementType({
    name: "panelTracker",
    isQuestion: true,
    isProgrammatic: true,
    allowedInPopup: false,
    icon: ["fal", "user-tag"],
    title: "Panel tracker",
    description: "Panel id tracker",
    responseContentType: "string",
    labelPropertyName: "label",
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "parameter",
        "type": "string",
        "inputType": "text",
        "group": "functional",
        "tabGroup": "basic",
        "default": null,
        "title": "Parameter",
        "description": "Enter the name of the parameter to track in the response data.",
        "multilingual": false,
    }, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Label",
        "description": "Select a label to identify this panel group tracker in the response data.",
        "multilingual": false,
    }],
});

export const pageJump = new QuestionnaireElementType({
    name: "pageJump",
    canJump: true,
    isQuestion: false,
    isProgrammatic: true,
    allowedInPopup: false,
    icon: ["fal", "arrow-to-right"],
    title: "Page jump",
    description: "Page jump",
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "nextPageId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "inputType": "questionnairePage",
        "group": "functional",
        "tabGroup": "basic",
        "default": null,
        "title": "Page",
        "description": "A named page to jump to next after this page.",
        "multilingual": false,
    }],
    isDeprecated: true,
});

export const sharingButton = new QuestionnaireElementType({
    name: "sharingButton",
    isQuestion: false,
    isProgrammatic: true,
    allowedInPopup: false,
    icon: ["fal", "share"],
    title: "Sharing button",
    description: "social media sharing",
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "sharingButton",
        "type": "list.string",
        "default": [],
        "group": "functional",
        "inputType": "ownOptionMany",
        "tabGroup": "basic",
        "valueOptions": ["facebook", "twitter", "linkedin", "pinterest", "reddit"],
        "valueOptionIcons": [["fab", "facebook"], ["fab", "x-twitter"], ["fab", "linkedin"], ["fab", "pinterest"], ["fab", "reddit"]],
        "title": "Sharing buttons",
        "description": "Which social media sharing buttons to show",
        "multilingual": false,
    }],
});

export const deviceInformation = new QuestionnaireElementType({
    name: "deviceInformation",
    isQuestion: true,
    isProgrammatic: true,
    allowedInPopup: false,
    icon: ["fal", "info-circle"],
    title: "Device information",
    description: "information about the device being used",
    responseContentType: "string",
    labelPropertyName: "label",
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "dataType",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["height", "width", "browser", "operatingSystem"],
        "valueOptionTexts": ["height", "width", "browser", "operating system"],
        "group": "functional",
        "tabGroup": "basic",
        "default": "width",
        "title": "Parameter",
        "description": "Select which data type you want to record.",
        "multilingual": false,
    }, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Label",
        "description": "Select a label to identify this element in the response data.",
        "multilingual": false,
    }],
});





/** ******************************************************************
 *
 * Content elements
 *
 ********************************************************************/
export const header = new QuestionnaireElementType({
    name: "header",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fal", "heading"],
    title: "Header",
    description: "A text header",
    labelPropertyName: "text",
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "text",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Header text",
        "description": "This is the text which appears in the header.",
        "multilingual": true,
    }, {
        "key": "size",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["1", "2", "3", "4"],
        "valueOptionTexts": ["Extra large", "Large", "Medium", "Small"],
        "group": "style",
        "tabGroup": "basic",
        "default": "2",
        "title": "Header size",
        "description": "The header size class to use.",
        "multilingual": false,
    }, {
        "key": "class",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["title", "subtitle"],
        "valueOptionTexts": ["Title", "Subtitle"],
        "group": "style",
        "tabGroup": "basic",
        "default": "title",
        "title": "Header style",
        "description": "The header style class to use.",
        "multilingual": false,
    }, {
        "key": "align",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["left", "center", "right"],
        "valueOptionIcons": ["align-left", "align-center", "align-right"],
        "group": "style",
        "tabGroup": "basic",
        "default": "",
        "title": "Alignment",
        "description": "How to align (left, center, right) the header.",
        "multilingual": false,
    }],
});

export const paragraph = new QuestionnaireElementType({
    name: "paragraph",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fal", "paragraph"],
    title: "Paragraph",
    description: "Display a paragraph of text.",
    labelPropertyName: "text",
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "text",
        "type": "string",
        "inputType": "textarea",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Paragraph content",
        "description": "This is the text which appears in the paragraph.",
        "multilingual": true,
    }, {
        "key": "size",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["small", "", "medium", "large"],
        "valueOptionTexts": ["Small", "Normal", "Medium", "Large"],
        "group": "style",
        "tabGroup": "basic",
        "default": "",
        "title": "Text size",
        "description": "The text size class to use.",
        "multilingual": false,
    }, {
        "key": "align",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["left", "center", "right"],
        "valueOptionIcons": ["align-left", "align-center", "align-right"],
        "group": "style",
        "tabGroup": "basic",
        "default": "",
        "title": "Alignment",
        "description": "How to align (left, center, right) the paragraph.",
        "multilingual": false,
    }, {
        "key": "contentType",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["raw", "markdown"],
        "valueOptionTexts": ["Plain text", "Markdown"],
        "group": "style",
        "tabGroup": "basic",
        "default": "markdown",
        "title": "Formatting",
        "description": "What format to display the text as. Markdown allows you to create simple styling for lists, URL's and emphasizing text.",
        "multilingual": false,
    }],
});

export const image = new QuestionnaireElementType({
    name: "image",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fal", "image"],
    title: "Image",
    description: "Image",
    labelPropertyName: "title",
    tabGroups: [basicTabGroup, extraTabGroup],
    propertiesList: [{
        "key": "imageAssetId",
        "type": "resourceId",
        "isResourceId": "asset",
        "inputType": "imageAssetId",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Image asset",
        "description": "An asset to use when displaying this image.",
        "multilingual": true,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "imageUrl",
        "type": "url",
        "inputType": "imageUrl",
        "group": "content",
        "tabGroup": "functional",
        "default": null,
        "title": "Image url",
        "description": "The url of the image to display.",
        "multilingual": true,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        "key": "showAttribution",
        "type": "boolean",
        "inputType": "switch",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Show attribution",
        "description": "Show the image asset's attribution.",
        "multilingual": false,
    }, {
        "key": "title",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Title",
        "description": "A title to display above the image.",
        "multilingual": true,
    }, {
        "key": "titleSize",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["small", "", "medium", "large"],
        "valueOptionTexts": ["Small", "Normal", "Medium", "Large"],
        "group": "style",
        "tabGroup": "extra",
        "default": "",
        "title": "Title text size",
        "description": "The text size class to use for the title.",
        "multilingual": false,
    }, {
        "key": "titleAlign",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["left", "center", "right"],
        "valueOptionIcons": ["align-left", "align-center", "align-right"],
        "group": "style",
        "tabGroup": "extra",
        "default": "left",
        "title": "Title alignment",
        "description": "How to align (left, center, right) the title.",
        "multilingual": false,
    }, {
        "key": "imageAlignment",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["left", "center", "right"],
        "valueOptionIcons": ["align-left", "align-center", "align-right"],
        "group": "style",
        "tabGroup": "basic",
        "default": "center",
        "title": "Image alignment",
        "description": "How to align the image (left, center, right) when the image is narrower than the page width and there is no caption text next to the image.",
        "multilingual": false,
    }, {
        "key": "imageWidth",
        "type": "number",
        "inputType": "slider",
        "group": "style",
        "tabGroup": "basic",
        "default": 100,
        "format": "percent",
        "limits": [0, 100],
        "step": 1,
        "title": "Image width",
        "description": "Choose how much space the image should take up as a percentage of page width. If you have caption text on the sides of the image, the maximum width of the image is limited to 70%. If you set this to zero, the image will not be scaled up.",
        "multilingual": false,
    }, {
        "key": "caption",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Caption",
        "description": "A caption to display next to the image.",
        "multilingual": true,
    }, {
        "key": "captionSize",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["small", "", "medium", "large"],
        "valueOptionTexts": ["Small", "Normal", "Medium", "Large"],
        "group": "style",
        "tabGroup": "extra",
        "default": "",
        "title": "Caption size",
        "description": "The text size class to use for the caption text.",
        "multilingual": false,
    }, {
        "key": "captionAlignment",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["left", "center", "right"],
        "valueOptionIcons": ["align-left", "align-center", "align-right"],
        "group": "style",
        "tabGroup": "extra",
        "default": "left",
        "title": "Caption text alignment",
        "description": "How to align (left, center, right) the caption text.",
        "multilingual": false,
    }, {
        "key": "captionPlacement",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["left", "below", "right"],
        "valueOptionTexts": ["Left", "Below", "Right"],
        "group": "style",
        "tabGroup": "extra",
        "default": "below",
        "title": "Caption placement",
        "description": "Place the caption to the left of, to the right of, or below the image.",
        "multilingual": false,
    }, {
        "key": "verticalAlignment",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["flex-start", "center", "flex-end"],
        "valueOptionIcons": ["arrow-to-top", "horizontal-rule", "arrow-to-bottom"],
        "group": "style",
        "tabGroup": "extra",
        "default": "flex-start",
        "title": "Vertical alignment",
        "description": "How to align the image and caption (top, center, bottom) when the caption is next to the image.",
        "multilingual": false,
    },
    ...DataUrlProperties],
});

export const audio = new QuestionnaireElementType({
    name: "audio",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fal", "music"],
    title: "Audio",
    description: "Audio",
    labelPropertyName: null,
    tabGroups: [basicTabGroup, extraTabGroup],
    propertiesList: [{
        "key": "audioAssetId",
        "type": "resourceId",
        "isResourceId": "asset",
        "inputType": "audioAssetId",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Audio asset",
        "description": "The audio asset to play in this audio player.",
        "multilingual": true,
    }, {
        "key": "showAttribution",
        "type": "boolean",
        "inputType": "switch",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Show attribution",
        "description": "Show the audio asset's attribution.",
        "multilingual": false,
    }, 
    ...DataUrlProperties],
});

export const youtube = new QuestionnaireElementType({
    name: "youtube",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fab", "youtube-square"],
    title: "Youtube video",
    description: "Youtube video",
    labelPropertyName: "titleText",
    tabGroups: [basicTabGroup, extraTabGroup],
    propertiesList: [{
        "key": "videoUrl",
        "type": "url",
        "inputType": "videoUrl",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Video url",
        "description": "The url of the video to display.",
        "multilingual": true,
    }, {
        "key": "showCaptions",
        "type": "boolean",
        "group": "functional",
        "tabGroup": "extra",
        "inputType": "switch",
        "default": false,
        "title": "Show captions",
        "description": "Automatically show captions for the video if available.",
        "multilingual": false,
    }, {
        "key": "captionLocale",
        "type": "string",
        "inputType": "languageOption",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Caption language",
        "description": "Which language should the captions be in. Leave this blank to use the users currently selected language.",
        "multilingual": true,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "titleText",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Video title text",
        "description": "A brief text summary of the video.",
        "multilingual": true,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    },
    ...DataUrlProperties],
});

export const vimeo = new QuestionnaireElementType({
    name: "vimeo",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fab", "vimeo-square"],
    title: "Vimeo video",
    description: "Vimeo video",
    labelPropertyName: "titleText",
    tabGroups: [basicTabGroup, extraTabGroup],
    propertiesList: [{
        "key": "videoId",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Video ID",
        "description": "The ID of the video to display.",
        "multilingual": true,
    }, {
        "key": "showCaptions",
        "type": "boolean",
        "group": "functional",
        "tabGroup": "extra",
        "inputType": "switch",
        "default": false,
        "title": "Show captions",
        "description": "Automatically show captions for the video if available.",
        "multilingual": false,
    }, {
        "key": "captionLocale",
        "type": "string",
        "inputType": "languageOption",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Caption language",
        "description": "Which language should the captions be in. Leave this blank to use the users currently selected language.",
        "multilingual": true,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "titleText",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Video title text",
        "description": "A brief text summary of the video.",
        "multilingual": true,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    },
    ...DataUrlProperties],
});

export const spacer = new QuestionnaireElementType({
    name: "spacer",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fal", "expand-wide"],
    title: "Spacer",
    description: "A vertical spacer",
    labelPropertyName: null,
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "height",
        "type": "number",
        "inputType": "integer",
        "group": "style",
        "tabGroup": "basic",
        "default": 20,
        "title": "Spacer height",
        "description": "This is the height in pixels of the spacer.",
        "multilingual": false,
    }, {
        "key": "showRule",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "basic",
        "default": false,
        "title": "Show horizontal rule",
        "description": "Whether to show a horizontal rule.",
        "multilingual": false,
    }, {
        "key": "ruleThickness",
        "type": "number",
        "inputType": "integer",
        "group": "style",
        "tabGroup": "basic",
        "default": 1,
        "title": "Rule thickness",
        "description": "This is the thickness in pixels of the rule.",
        "multilingual": false,
    }, {
        "key": "ruleWidth",
        "type": "number",
        "inputType": "slider",
        "group": "style",
        "tabGroup": "basic",
        "default": 100,
        "format": "percent",
        "limits": [0, 100],
        "step": 1,
        "ticks": [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
        "title": "Rule width",
        "description": "The width in percent of the rule.",
        "multilingual": false,
    }, {
        "key": "ruleColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "basic",
        "default": "#000000",
        "title": "Rule color",
        "description": "The color to use for the rule. Use the color selector or specify a hex value.",
        "multilingual": false,
    }, {
        "key": "ruleStyle",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["solid", "dashed", "dotted", "double"],
        "valueOptionTexts": ["Solid", "Dashed", "Dotted", "Double"],
        "group": "style",
        "tabGroup": "basic",
        "default": "solid",
        "title": "Rule style",
        "description": "The style for drawing the rule.",
        "multilingual": false,
    }],
});

export const popupButton = new QuestionnaireElementType({
    name: "popupButton",
    hasPopup: true,
    isQuestion: false,
    allowedInPopup: false,
    icon: ["fal", "comment-alt-dots"],
    title: "Popup button",
    description: "A button that opens a pop-up",
    labelPropertyName: "buttonText",
    tabGroups: [basicTabGroup],
    propertiesList: [...ButtonProperties,
    {
        // DEPRECIATED
        // DEPRECIATED
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Drawbutton label",
        "description": "The text to display on the popup button.",
        "multilingual": true,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "textColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "basic",
        "default": "#363636",
        "title": "Text color",
        "description": "The color to use for the popup button's text and icon. Use the color selector or specify a hex value.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        "key": "popupId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "group": "hidden",
        "tabGroup": "basic",
        "default": null,
        "multilingual": false,
        "hiddenFromInterface": true,
    }],
});

export const showMapButton = new QuestionnaireElementType({
    name: "showMapButton",
    hasPopup: false,
    isQuestion: false,
    allowedInPopup: false,
    icon: ["fal", "map"],
    title: "Show map button",
    description: "A button that opens the map",
    labelPropertyName: "buttonText",
    tabGroups: [basicTabGroup],
    propertiesList: [...ButtonProperties],
});

/** ******************************************************************
 *
 * Question elements
 *
 ********************************************************************/
export const textEntry = new QuestionnaireElementType({
    name: "textEntry",
    isQuestion: true,
    sortIndex: 5,
    allowedInPopup: true,
    icon: ["fal", "pen"],
    title: "Open Question",
    description: "Open question",
    responseContentType: "string",
    labelPropertyName: "label",
    maxStringLengthPropertyName: "maxLength",
    tabGroups: [basicTabGroup, extraTabGroup, budgetingTabGroup],
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, {
        "key": "placeholder",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Placeholder text",
        "description": "Text to display when the response field is empty.",
        "multilingual": true,
    }, {
        "key": "validateEmail",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Validation",
        "description": "Validate that the response is a correctly formatted email address.",
        "multilingual": false,
    }, {
        "key": "icon",
        "hiddenFromInterface": true,
        "type": "list.string",
        "inputType": "iconOption",
        "group": "style",
        "tabGroup": "extra",
        "default": null,
        "title": "Placeholder icon",
        "description": "An icon to display inside the response field. Only for single line responses.",
        "multilingual": false,
    }, {
        "key": "iconClass",
        "hiddenFromInterface": true,
        "type": "string",
        "inputType": "iconClassOption",
        "group": "style",
        "tabGroup": "extra",
        "default": "fal",
        "title": "Placeholder icon weight",
        "description": "The font weight (light, regular, bold) to use for the icon.",
        "multilingual": false,
    }, {
        "key": "required",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Required",
        "description": "The respondent must fill in a response before proceeding.",
        "multilingual": false,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "multiLine",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Multiline",
        "description": "This response can be multiple lines of text.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        "key": "defaultRows",
        "type": "integer",
        "inputType": "integer",
        "group": "style",
        "tabGroup": "basic",
        "default": 2,
        "min": 1,
        "title": "Number of rows",
        "description": "The number of rows to display when this is a multi-line input.",
        "unit": "rows",
        "multilingual": false,
    }, {
        "key": "maxLength",
        "type": "integer",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "basic",
        "default": 1000,
        "title": "Maximum text length",
        "description": "The maximum length of the response that this input will accept.",
        "unit": "characters",
        "multilingual": false,
    }, {
        "key": "hasCounter",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "basic",
        "default": false,
        "title": "Character counter",
        "description": "Show a character counter when a maximum text length has been specified.",
        "multilingual": false,
    }, {
        "key": "analysisStopWords",
        "type": "string",
        "group": "hidden",
        "tabGroup": "basic",
        "inputType": "ownOption",
        "default": null,
        "title": "Exclude words",
        "description": "Enter words to exclude from the word cloud",
        "multilingual": false,
        "hiddenFromInterface": true,
        // "doNotPublish": true, // TODO: Make this work questionnairePublication.js, generateQuestionnaitePublication()
    }, {
        // This is the id of the linkage between elements (it's not the elementId)!
        "key": "linkedElementId",
        "type": "string",
        "inputType": "string",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Comments for other element types",
        "description": "Comments for 'Other' as an answer option for radioButtonList, checkboxList.",
        "hiddenFromInterface": true,
        "multilingual": false,
    }, 
    ReportRespondentProperty("textEntry"), ParticipatoryIdeaProperty("textEntry")],
});

export const budgeting = new QuestionnaireElementType({
    name: "budgeting",
    isQuestion: true,
    sortIndex: 9,
    allowedInPopup: true,
    needsLicense: "feature:questionnaire:budgeting",
    icon: ["fal", "comments-alt-dollar"],
    title: "Budgeting",
    description: "Budgeting question",
    responseContentType: "list.number", // List of cost totals (unit cost * units allocated)
    labelPropertyName: "label",
    optionsPropertyName: "itemNames",
    optionDataPropertyNames: {
        "label": "items",
        "cost": "itemCosts",
    },
    tabGroups: [basicTabGroup, extraTabGroup],
    parallelGroups: [{
        group: "itemGroup",
        title: "Items",
        description: "Items that can be selected as a response.",
        labelPropertyName: "items",
        tabGroups: [basicTabGroup, extraTabGroup],
        tabGroup: "basic",
    }],
    getResponse: function (response) {
        let parsedValue;
        try {
            parsedValue = JSON.parse(response);
        } catch (e) {
            parsedValue = response;
        }
        return parsedValue;
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "tabGroup": "basic",
        "group": "content",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the budgeting element.",
        "multilingual": true,
    }, {
        "key": "budget",
        "type": "number",
        "inputType": "number",
        "tabGroup": "basic",
        "group": "content",
        "default": null,
        "title": "Budget",
        "description": "The total amount of available funds.",
        "multilingual": false,
    }, {
        "key": "startingValue",
        "type": "number",
        "inputType": "number",
        "tabGroup": "basic",
        "group": "content",
        "default": null,
        "title": "Starting value",
        "description": "Select a starting value for the budget.",
        "multilingual": false,
    }, {
        "key": "unit",
        "type": "string",
        "inputType": "text",
        "tabGroup": "basic",
        "group": "content",
        "default": null,
        "title": "Unit",
        "description": "The unit of the funds.",
        "multilingual": true,
    }, {
        "key": "barColor",
        "type": "string",
        "inputType": "color",
        "tabGroup": "basic",
        "group": "content",
        "default": null,
        "title": "Bar color",
        "description": "Choose a custom color for the budget bar",
        "multilingual": false,
    }, {
        "key": "style",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["checkbox", "numberEntry"],
        "valueOptionTexts": ["One time", "Many times"],
        "tabGroup": "basic",
        "group": "content",
        "default": "checkbox",
        "title": "Select items",
        "description": "Whether you can select an item one time or many times.",
        "multilingual": false,
    }, {
        "key": "unitFirst",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Unit first",
        "description": "Place the unit symbol before the number.",
        "multilingual": true,
    }, {
        "key": "hideProgressbar",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Hide progress bar",
        "description": "Hide budget limit as a progress bar.",
        "multilingual": false,
    }, {
        "key": "isBalancingBudget",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Balancing budget",
        "description": "Choose this to allow spending below zero. The limit for spending below zero is the same as for spending above zero.",
        "multilingual": false,
    }, {
        "key": "reverseDirection",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Reverse direction",
        "description": "Choose this have the bar fill up from the right.",
        "multilingual": false,
    }, {
        "key": "hideItemCosts",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Hide item costs",
        "description": "Hide the cost for each item.",
        "multilingual": false,
    }, {
        "key": "randomize",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "functional",
        "default": false,
        "title": "Randomize",
        "description": "Randomize the order of the items for each respondent.",
        "multilingual": false,
    }, {
        "key": "items",
        "type": "list.string",
        "inputType": "text",
        "tabGroup": "basic",
        "group": "itemGroup",
        "default": [],
        "defaultInsert": "",
        "title": "Item title",
        "description": "The title identifying this option.",
        "multilingual": true,
    }, {
        "key": "itemCosts",
        "type": "list.number",
        "inputType": "number",
        "tabGroup": "basic",
        "group": "itemGroup",
        "default": [],
        "defaultInsert": "",
        "title": "Item cost",
        "description": "The cost of selecting this option.",
        "multilingual": false,
    }, {
        "key": "itemDescription",
        "type": "list.string",
        "inputType": "textarea",
        "native.rows": 3,
        "tabGroup": "extra",
        "group": "itemGroup",
        "default": [],
        "defaultInsert": "",
        "title": "Item description",
        "description": "The description explaining this option.",
        "multilingual": true,
    },  {
        "key": "itemColors",
        "type": "list.string",
        "inputType": "color",
        "tabGroup": "extra",
        "group": "itemGroup",
        "default": [],
        "defaultInsert": null,
        "title": "Item color",
        "description": "Choose a custom color for this item to show in the budget bar.",
        "multilingual": false,
    }, {
        "key": "itemNames",
        "type": "list.string",
        "inputType": "text",
        "tabGroup": "response",
        "group": "itemGroup",
        "default": [],
        "defaultInsert": "",
        "title": "Item data name",
        "description": "The data name identifying this option.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }, 
    // DEPRECIATED
    // DEPRECIATED
    {
        "key": "analysisFormatType",
        "type": "string",
        "group": "hidden",
        "tabGroup": "basic",
        "inputType": "ownOption",
        "valueOptions": ["support", "money", "votes"],
        "valueOptionTexts": ["Amount of support", "Amount of money", "Number of votes"],
        "default": "votes",
        "title": "Results format type",
        "description": "The results format to use for displaying the aggregate results.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
    },
    // DEPRECIATED
    // DEPRECIATED
    ],
});


export const checkbox = new QuestionnaireElementType({
    name: "checkbox",
    canJump: true,
    isQuestion: true,
    sortIndex: 11,
    allowedInPopup: true,
    icon: ["fal", "check-square"],
    title: "Checkbox",
    description: "Checkbox question",
    responseContentType: "boolean",
    labelPropertyName: "label",
    tabGroups: [basicTabGroup, extraTabGroup],
    getResponse: function (response) {
        let parsedValue;
        try {
            parsedValue = JSON.parse(response);
        } catch (e) {
            parsedValue = response;
        }
        return parsedValue;
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the checkbox.",
        "multilingual": true,
    }, {
        "key": "text",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Checkbox text",
        "description": "The text to show next to the checkbox.",
        "multilingual": true,
    }, {
        "key": "required",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Required",
        "description": "The respondent must check this checkbox before proceeding.",
        "multilingual": false,
    }, {
        "key": "nextPageId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "inputType": "questionnairePage",
        "notAllowedInPopups": true,
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Jump to page",
        "description": "Select a named page to jump to after this page, when the checkbox is selected.",
        "multilingual": false,
        "hiddenFromInterface": true, // isDeprecated
        "isDeprecated": true,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "defaultValue",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Default value",
        "description": "Whether this checkbox is initially checked when a respondent loads the page.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }],
});

export const datepicker = new QuestionnaireElementType({
    name: "datepicker",
    isDeprecated: true,
    isQuestion: true,
    sortIndex: 12,
    allowedInPopup: true,
    icon: ["fal", "calendar"],
    title: "Date Picker",
    description: "Date picker question",
    responseContentType: "string", // "year-month-date"
    labelPropertyName: "label",
    tabGroups: [basicTabGroup],
    getResponse: function (response) {
        return response;
    },
    propertiesList: [{
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the date selector.",
        "multilingual": true,
    }, {
        "key": "restrictMinDate",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "basic",
        "default": false,
        "title": "Restrict Minimum Date",
        "description": "Select this if you want to restrict the earliest date that can be chosen.",
        "multilingual": false,
    }, {
        "key": "minDate",
        "type": "date",
        "inputType": "dateLocal",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Minimum date",
        "description": "Earlier dates cannot be selected.",
        "multilingual": false,
        "showIfProperty": {
            "key": "restrictMinDate",
            "value": [true],
        },
    }, {
        "key": "restrictMaxDate",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "basic",
        "default": false,
        "title": "Restrict Maximum Date",
        "description": "Select this if you want to restrict the latest date that can be chosen.",
        "multilingual": false,
    }, {
        "key": "maxDate",
        "type": "date",
        "inputType": "dateLocal",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Maximum date",
        "description": "Later dates cannot be selected.",
        "multilingual": false,
        "showIfProperty": {
            "key": "restrictMaxDate",
            "value": [true],
        },
    }, {
        "key": "firstDayOfWeek",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "basic",
        "default": false,
        "title": "Start week with Sunday",
        "description": "Sunday is displayed as the first day of the week",
        "multilingual": false,
    }],
});


export const checkboxList = new QuestionnaireElementType({
    name: "checkboxList",
    isQuestion: true,
    sortIndex: 2,
    allowedInPopup: true,
    isMultipleChoice: MultipleChoice.Any,
    tabGroups: [basicTabGroup, extraTabGroup],
    parallelGroups: [{
        group: "optionGroup",
        title: "Options",
        description: "Options that can be selected as a response.",
        labelPropertyName: "options",
        tabGroups: [basicTabGroup, extraTabGroup],
        tabGroup: "basic",
    }],
    icon: ["fal", "tasks"],
    title: "Checkbox list",
    description: "A list of checkbox questions",
    responseContentType: "list.boolean",
    labelPropertyName: "label",
    optionsPropertyName: "optionNames",
    optionDataPropertyNames: {
        "label": "options",
    },
    getResponse: function (response) {
        const o = this.properties?.getValue?.("options");
        let parsedValue;
        // JSON.parse() does not throw an error for array of length 1;
        // e.g. JSON.parse([true]) returns true;
        if (typeof(response) === "string") {
            try {
                parsedValue = JSON.parse(response);
            } catch(e) {
                parsedValue = Array.isArray(response) ? response : [];
            }
        } else {
            parsedValue = Array.isArray(response) ? response : [];
        }
        
        return parsedValue.map(
            (r: any, i: number) => (r ? o?.[i] : null)
        ).filter(
            (r: any) => (r !== null)
        );
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the list.",
        "multilingual": true,
    }, {
        "key": "style",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["list", "row", "buttons", "images", "gridView"],
        "valueOptionTexts": ["List", "Row", "Buttons", "Images", "Grid view"],
        "group": "style",
        "tabGroup": "basic",
        "default": "list",
        "title": "List style",
        "description": "How to arrange the layout of the list of checkboxes.",
        "multilingual": false,
    }, {
        "key": "hideNames",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide option names",
        "description": "When the selected list style is Images, this will hide the option names.",
        "multilingual": false,
    }, {
        "key": "randomize",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Randomize",
        "description": "Randomize the order of the checkboxes for each respondent.",
        "multilingual": false,
    }, {
        "key": "keepLast",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Keep last option in place",
        "description": "Keep the last checkbox in place if 'Randomize' is selected.",
        "multilingual": false,
    }, {
        "key": "gridColumnNumber",
        "type": "number",
        "inputType": "slider",
        "group": "style",
        "tabGroup": "extra",
        "default": 3,
        "limits": [1, 5],
        "step": 1,
        "ticks": [1, 2, 3, 4, 5],
        "title": "Image column number",
        "description": "If the list style is set to be in grid view, this sets the number of columns for the grid view.",
        "multilingual": false,
    }, {
        "key": "minRequired",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Minimum required",
        "description": "The respondent must check at least this many checkboxes before proceeding.",
        "multilingual": false,
    }, {
        "key": "maxAllowed",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum allowed",
        "description": "The respondent may check at most this many checkboxes before proceeding.",
        "multilingual": false,
    }, {
        "key": "options",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Option",
        "description": "The label for this option.",
        "multilingual": true,
    }, {
        "key": "optionImages",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "inputType": "imageAssetId",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Option image",
        "description": "The image for this option.",
        "multilingual": true,
        "excludedByDefault": true,
    }, {
        "key": "optionNames",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Option identifier",
        "description": "The data name identifying this option.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }, {
        // This is the id of the linkage between elements (it's not the elementId)!
        "key": "optionLinkedTextEntry",
        "type": "list.string",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Add comment box to the last option",
        "description": "Show a comment box when the last option is selected.",
        "multilingual": false,
        "excludedByDefault": true,
    }],
});

export const radioButtonList = new QuestionnaireElementType({
    name: "radioButtonList",
    canJump: true,
    isQuestion: true,
    sortIndex: 1,
    allowedInPopup: true,
    isMultiQuestion: false,
    isMultipleChoice: MultipleChoice.One,
    tabGroups: [basicTabGroup, extraTabGroup, budgetingTabGroup],
    parallelGroups: [{
        group: "optionGroup",
        title: "Options",
        description: "Options that can be selected as a response.",
        labelPropertyName: "options",
        tabGroups: [basicTabGroup, extraTabGroup],
        tabGroup: "basic",
    }],
    icon: ["fal", "list-ul"],
    title: "Radio button list",
    description: "A list of radio buttons",
    responseContentType: "integer",
    labelPropertyName: "label",
    optionsPropertyName: "optionNames",
    optionDataPropertyNames: {
        "label": "options",
    },
    getResponse: function (response) {
        const o = this.properties?.getValue?.("options");
        return ( 
            Number.isFinite(+response) &&
            o?.[+response]
        ) || undefined;
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the radio buttons.",
        "multilingual": true,
    }, {
        "key": "style",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["list", "row", "buttons", "dropdown", "images", "gridView"],
        "valueOptionTexts": ["List", "Row", "Buttons", "Dropdown", "Images", "Grid View"],
        "group": "style",
        "tabGroup": "basic",
        "default": "list",
        "title": "List style",
        "description": "How to arrange the layout of the list of radio buttons.",
        "multilingual": false,
    }, {
        "key": "required",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Required",
        "description": "The respondent must give a response before proceeding.",
        "multilingual": false,
    }, {
        "key": "hideNames",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide option names",
        "description": "When the selected list style is Images, this will hide the option names.",
        "multilingual": false,
    }, {
        "key": "randomize",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Randomize",
        "description": "Randomize the order of the radio buttons for each respondent.",
        "multilingual": false,
    }, {
        "key": "keepLast",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Keep last option in place",
        "description": "Keep the last option in place if 'Randomize' is selected.",
        "multilingual": false,
    }, {
        "key": "gridColumnNumber",
        "type": "number",
        "inputType": "slider",
        "group": "style",
        "tabGroup": "extra",
        "default": 3,
        "limits": [1, 5],
        "step": 1,
        "ticks": [1, 2, 3, 4, 5],
        "title": "Image column number",
        "description": "If the list style is set to be in grid view, this sets the number of columns for the grid view.",
        "multilingual": false,
    }, {
        "key": "options",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Option",
        "description": "The label for this option.",
        "multilingual": true,
    }, {
        "key": "optionImages",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "inputType": "imageAssetId",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Option image",
        "description": "The image for this option.",
        "multilingual": true,
        "excludedByDefault": true,
    }, {
        "key": "optionOriginLonLatZoom",
        "type": "list.list.number",
        "inputType": "olLonLatZoom",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": null,
        "title": "Option location",
        "description": "The map location and zoom to move to, when this option is selected. Use the map to adjust the view and then select 'Set view' to set the coordinates and zoom level.",
        "multilingual": false,
        "excludedByDefault": true,
    }, {
        "key": "optionNextPageIds",
        "type": "list.resourceId",
        "isResourceIdList": "questionnaireElementGroup",
        "inputType": "questionnairePage",
        "notAllowedInPopups": true,
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": null,
        "title": "Jump to page",
        "description": "Select a named page to jump to after this page, when this option is selected.",
        "multilingual": false,
        "excludedByDefault": true,
        "hiddenFromInterface": true, // deprecated
        "isDeprecated": true,
    },
    // DEPRECIATED
    // {
    //     "key": "optionSova3dProject",
    //     "type": "list.string",
    //     "inputType": "text",
    //     "notAllowedInPopups": true,
    //     "group": "optionGroup",
    //     "tabGroup": "extra",
    //     "default": [],
    //     "defaultInsert": null,
    //     "title": "Show Sova3D project",
    //     "description": "Select a Sova3D project to show on this page, when this option is selected.",
    //     "multilingual": false,
    // }, 
    {
        "key": "optionNames",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Option identifier",
        "description": "The data name identifying this option.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }, {
        // This is the id of the linkage between elements (it's not the elementId)!
        "key": "optionLinkedTextEntry",
        "type": "list.string",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Add comment box to the last option",
        "description": "Show a comment box when the last option is selected.",
        "multilingual": false,
        "excludedByDefault": true,
    }, ReportRespondentProperty("radioButtonList"), ParticipatoryIdeaProperty("radioButtonList"),
]}
);

export const radioButtonPoll = new QuestionnaireElementType({
    name: "radioButtonPoll",
    canJump: true,
    isQuestion: true,
    sortIndex: 7,
    allowedInPopup: true,
    isMultiQuestion: false,
    allowAggregation: true,
    isMultipleChoice: MultipleChoice.One,
    tabGroups: [basicTabGroup, extraTabGroup],
    parallelGroups: [{
        group: "optionGroup",
        title: "Options",
        description: "Options that can be selected as a response.",
        labelPropertyName: "options",
        tabGroups: [basicTabGroup, extraTabGroup],
        tabGroup: "basic",
    }],
    canShareResponses: true,
    icon: ["fal", "poll-people"],
    title: "Poll",
    description: "A single choice poll",
    responseContentType: "integer",
    labelPropertyName: "label",
    optionsPropertyName: "optionNames",
    optionDataPropertyNames: {
        "label": "options",
    },
    getResponse: function (response) {
        const o = this.properties?.getValue?.("options");
        return (
            Number.isFinite(+response) &&
            o?.[+response]
        ) || undefined;
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the radio buttons.",
        "multilingual": true,
    }, {
        "key": "required",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Required",
        "description": "The respondent must give a response before proceeding.",
        "multilingual": false,
    }, {
        "key": "chartType",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["pie", "doughnut", "bar", "horizontalBar"],
        "valueOptionTexts": ["Pie", "Doughnut", "Bar", "Horizontal bar"],
        "group": "functional",
        "tabGroup": "basic",
        "default": "doughnut",
        "title": "Results chart type",
        "description": "The type of the chart to use for displaying the aggregate results.",
        "multilingual": false,
    }, {
        "key": "style",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["list", "row", "buttons", "dropdown", "images", "gridView"],
        "valueOptionTexts": ["List", "Row", "Buttons", "Dropdown", "Images", "Grid view"],
        "group": "style",
        "tabGroup": "extra",
        "default": "list",
        "title": "List style",
        "description": "How to arrange the layout of the list of radiobuttons.",
        "multilingual": false,
    }, {
        "key": "hideNames",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide option names",
        "description": "When the selected list style is Images, this will hide the option names.",
        "multilingual": false,
    }, {
        "key": "randomize",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Randomize",
        "description": "Randomize the order of the radio buttons for each respondent.",
        "multilingual": false,
    }, {
        "key": "keepLast",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Keep last option in place",
        "description": "Keep the last option in place if 'Randomize' is selected.",
        "multilingual": false,
    }, {
        "key": "gridColumnNumber",
        "type": "number",
        "inputType": "slider",
        "group": "style",
        "tabGroup": "extra",
        "default": 3,
        "limits": [1, 5],
        "step": 1,
        "ticks": [1, 2, 3, 4, 5],
        "title": "Image column number",
        "description": "If the list style is set to be in grid view, this sets the number of columns for the grid view.",
        "multilingual": false,
    }, {
        "key": "requireExplicitConsent",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Require explicit consent",
        "description": "Require respondents to explicitly give consent for response inclusion in aggregate results.",
        "multilingual": false,
    }, {
        "key": "allowResponseChange",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": true,
        "title": "Allow response change",
        "description": "Allow respondents to change their response once submitted.",
        "multilingual": false,
    }, {
        "key": "requireResponseForResults",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": true,
        "title": "Require response for results",
        "description": "Require respondents to submit a response before displaying the aggregate results.",
        "multilingual": false,
    }, {
        "key": "options",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Option",
        "description": "The label for this option.",
        "multilingual": true,
    }, {
        "key": "optionColors",
        "type": "list.string",
        "inputType": "color",
        "group": "optionGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Graph color",
        "description": "The color to use for this option in the response graphs. Use the color selector or specify a hex value.",
        "multilingual": false,
        "autoFill": "palette",
    }, {
        "key": "optionImages",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "inputType": "imageAssetId",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Option image",
        "description": "The image for this option.",
        "multilingual": true,
        "excludedByDefault": true,
    }, {
        "key": "optionOriginLonLatZoom",
        "type": "list.list.number",
        "inputType": "olLonLatZoom",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": null,
        "title": "Option location",
        "description": "The map location and zoom to move to, when this option is selected. Use the map to adjust the view and then select 'Set view' to set the coordinates and zoom level.",
        "multilingual": false,
        "excludedByDefault": true,
    }, {
        "key": "optionNextPageIds",
        "type": "list.resourceId",
        "isResourceIdList": "questionnaireElementGroup",
        "inputType": "questionnairePage",
        "notAllowedInPopups": true,
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": null,
        "title": "Jump to page",
        "description": "Select a named page to jump to after this page, when this option is selected.",
        "multilingual": false,
        "excludedByDefault": true,
        "hiddenFromInterface": true, // deprecated
        "isDeprecated": true,
    },
    // DEPRECIATED
    // {
    //     "key": "optionSova3dProject",
    //     "type": "list.string",
    //     "inputType": "text",
    //     "notAllowedInPopups": true,
    //     "group": "optionGroup",
    //     "tabGroup": "extra",
    //     "default": [],
    //     "defaultInsert": null,
    //     "title": "Show Sova3D project",
    //     "description": "Select a Sova3D project to show on this page, when this option is selected.",
    //     "multilingual": false,
    // },
    {
        "key": "optionNames",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Option identifier",
        "description": "The data name identifying this option.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }],
});

// export const checkboxGrid = new QuestionnaireElementType({
//     name: "checkboxGrid",
//     isQuestion: true,
//     icon: ["custom", "checkboxGrid"],
//     title: "Checkbox grid",
//     description: "A grid of checkbox questions",
//     responseContentType: "list.string",
//     propertiesList: [{
//         "key": "label",
//         "type": "string",
//         "inputType": "text",
//         "group": "content",
//         "default": null,
//         "title": "Question label",
//         "description": "The label to show above the checkbox.",
//         "multilingual": true,
//     }, {
//         "key": "questions",
//         "type": "list.string",
//         "inputType": "text",
//         "group": "parallel",
//         "default": [""],
//         "defaultInsert": "",
//         "title": "Questions",
//         "description": "The label for a row.",
//         "multilingual": true,
//     }, {
//         "key": "options",
//         "type": "list.string",
//         "inputType": "text",
//         "group": "parallel",
//         "default": [""],
//         "defaultInsert": "",
//         "title": "Options",
//         "description": "The data values for the response data, can also be used as headers for the columns.",
//         "multilingual": true,
//     }, {
//         "key": "headerLeft",
//         "type": "string",
//         "inputType": "text",
//         "group": "content",
//         "default": null,
//         "title": "Left column header",
//         "description": "A custom header to show for the column if the options are not used as headers.",
//         "multilingual": true,
//     }, {
//         "key": "headerCenter",
//         "type": "string",
//         "inputType": "text",
//         "group": "content",
//         "default": null,
//         "title": "Center column header",
//         "description": "A custom header to show for the column if the options are not used as headers.",
//         "multilingual": true,
//     }, {
//         "key": "headerRight",
//         "type": "string",
//         "inputType": "text",
//         "group": "content",
//         "default": null,
//         "title": "Right column header",
//         "description": "A custom header to show for the column if the options are not used as headers.",
//         "multilingual": true,
//     }, {
//         "key": "randomize",
//         "type": "boolean",
//         "inputType": "checkbox",
//         "group": "functional",
//         "default": false,
//         "title": "Randomize",
//         "description": "Randomize the order of the checkboxes for each respondent.",
//         "multilingual": false,
//     }, {
//         "key": "keepLast",
//         "type": "boolean",
//         "inputType": "checkbox",
//         "group": "functional",
//         "default": false,
//         "title": "Keep last option in place",
//         "description": "Keep the last checkbox in place if 'Randomize' is selected.",
//         "multilingual": false,
//     }, {
//         "key": "showOptionHeaders",
//         "type": "boolean",
//         "inputType": "checkbox",
//         "group": "functional",
//         "default": false,
//         "title": "Show options as headers",
//         "description": "Use the option values as headers for the grid columns.",
//         "multilingual": false,
//     }],
// });

export const radioButtonGrid = new QuestionnaireElementType({
    name: "radioButtonGrid",
    isQuestion: true,
    sortIndex: 3,
    allowedInPopup: true,
    icon: ["custom", "radioButtonGrid"],
    title: "Radio button grid",
    description: "A grid of radio buttons",
    responseContentType: "list.integer",
    labelPropertyName: "label",
    tabGroups: [basicTabGroup, extraTabGroup],
    parallelGroups: [{
        group: "questionGroup",
        title: "Sub-questions",
        description: "Questions that appear in the rows.",
        labelPropertyName: "questions",
        tabGroups: [basicTabGroup],
        tabGroup: "basic",
    }, {
        group: "optionGroup",
        title: "Options",
        description: "Options that can be selected as a response.",
        labelPropertyName: "options",
        tabGroups: [basicTabGroup],
        tabGroup: "basic",
    }],
    optionsPropertyName: "questionNames",
    optionDataPropertyNames: {
        "label": "questions",
    },
    getResponse: function (response) {
        const o = this.properties?.getValue?.("options");
        const q = this.properties?.getValue?.("questions");
        let parsedValue;
         // JSON.parse() does not throw an error for array of length 1;
        // e.g. JSON.parse([0]) returns 0;
        if (typeof(response) === "string") {
            try {
                parsedValue = JSON.parse(response);
            } catch(e) {
                parsedValue = Array.isArray(response) ? response : [];
            }
        } else {
            parsedValue = Array.isArray(response) ? response : [];
        }
        return parsedValue.map(
            (r:  any, i: number) => (+r !== -1 ? `${q?.[i]}: ${o?.[+r]}` : null)
        ).filter(
            (r: any) => (r !== null)
        );
    },
    propertiesList: [{
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the grid.",
        "multilingual": true,
    }, {
        "key": "minRequired",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Minimum required",
        "description": "The respondent must answer at least this many rows before proceeding.",
        "multilingual": false,
    }, {
        "key": "maxAllowed",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum allowed",
        "description": "The respondent may answer at most this many rows before proceeding.",
        "multilingual": false,
    }, 
    ...PublicQuestionElementProperties,
    {
        "key": "randomize",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Randomize",
        "description": "Randomize the order of the checkboxes for each respondent.",
        "multilingual": false,
    }, {
        "key": "keepLast",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Keep last option in place",
        "description": "Keep the last checkbox in place if 'Randomize' is selected.",
        "multilingual": false,
    }, {
        "key": "priority",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Priority assessment",
        "description": "Respondent can only choose each option (column) once.",
        "multilingual": false,
    }, {
        "key": "headerLeft",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Left column header",
        "description": "A custom header to show for the column if the options are not used as headers.",
        "multilingual": true,
    }, {
        "key": "headerCenter",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Center column header",
        "description": "A custom header to show for the column if the options are not used as headers.",
        "multilingual": true,
    }, {
        "key": "headerRight",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Right column header",
        "description": "A custom header to show for the column if the options are not used as headers.",
        "multilingual": true,
    }, {
        "key": "hideOptionHeaders",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide individual column headers",
        "description": "Do not show the option labels as headers for the grid columns.",
        "multilingual": false,
    }, {
        "key": "questions",
        "type": "list.string",
        "inputType": "text",
        "group": "questionGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Sub-question",
        "description": "The label for this row.",
        "multilingual": true,
    }, {
        "key": "questionNames",
        "type": "list.string",
        "inputType": "text",
        "group": "questionGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Sub-question identifier",
        "description": "The data name identifying this question.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }, {
        "key": "options",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Option",
        "description": "The value for this column. This can also be used as the header for this column.",
        "multilingual": true,
    }, {
        "key": "optionNames",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Option identifier",
        "description": "The data names identifying this option.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }, 
    // DEPRECIATED
    // DEPRECIATED
    {
        "key": "analysisFormatType",
        "type": "string",
        "group": "hidden",
        "tabGroup": "basic",
        "inputType": "ownOption",
        "valueOptions": ["percentDataByOptionByQuestion", "countDataByOptionByQuestion"],
        "valueOptionTexts": ["Percentage of Question", "Respondent Count"],
        "default": "percentDataByOptionByQuestion",
        "title": "Results format type",
        "description": "The results format to use for displaying the aggregate results.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
    },
    // DEPRECIATED
    // DEPRECIATED
    ],
});

export const numberEntry = new QuestionnaireElementType({
    name: "numberEntry",
    isQuestion: true,
    sortIndex: 6,
    allowedInPopup: true,
    icon: ["fal", "pencil-ruler"],
    title: "Number question",
    description: "Number question",
    responseContentType: "number",
    labelPropertyName: "label",
    minValuePropertyName: "minValue",
    maxValuePropertyName: "maxValue",
    tabGroups: [basicTabGroup],
    getResponse: function (response) {
        return Number.isFinite(+response) ? +response : undefined;
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the number entry question.",
        "multilingual": true,
    }, {
        "key": "required",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Required",
        "description": "The respondent must enter a value before proceeding.",
        "multilingual": false,
    }, {
        "key": "minValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "default": null,
        "title": "Minimum value",
        "description": "The minimum value that is accepted by the input, if there is any input.",
        "multilingual": false,
    }, {
        "key": "maxValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "default": null,
        "title": "Maximum value",
        "description": "The maximum value that is accepted by the input, if there is any input.",
        "multilingual": false,
    }, {    
        "key": "stepValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "title": "Step size",
        "description": "The step size for the input when the respondent using the + and - buttons.",
        "default": 1,
        "multilingual": false,
    }, {
        "key": "defaultValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "default": null,
        "title": "Default value",
        "description": "The value that is shown initially in the input when a respondent loads the page.",
        "multilingual": false,
    }, {
        "key": "hideDefaultValue",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Hide default value",
        "description": "Select this to hide the default value when no response has been given.",
        "multilingual": false,
    }],
});

export const slider = new QuestionnaireElementType({
    name: "slider",
    isQuestion: true,
    sortIndex: 4,
    allowedInPopup: true,
    icon: ["fal", "sliders-h"],
    title: "Slider",
    description: "Slider question",
    responseContentType: "number",
    labelPropertyName: "label",
    minValuePropertyName: "minValue",
    maxValuePropertyName: "maxValue",
    tabGroups: [basicTabGroup, extraTabGroup],
    getResponse: function (response) {
        return Number.isFinite(+response) ? +response : undefined;
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the slider.",
        "multilingual": true,
    }, {    
        "key": "minLabel",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "title": "Minimum label",
        "description": "The label for the minimum end of the slider",
        "default": null,
        "multilingual": true,
    }, {    
        "key": "maxLabel",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "title": "Maximum label",
        "description": "The label for the maximum end of the slider",
        "default": null,
        "multilingual": true,
    }, {
        "key": "labelPosition",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["above", "beside", "below"],
        "valueOptionTexts": ["Above", "Beside", "Below"],
        "group": "style",
        "tabGroup": "extra",
        "default": "beside",
        "title": "Label position",
        "description": "Where to place the labels relative to the slider.",
        "multilingual": false,
    }, {
        "key": "hideIndicatorFill",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide fill",
        "description": "Hide the left hand side fill of the slider.",
        "multilingual": false,
    }, {
        "key": "showValue",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Show value",
        "description": "Show the current value of the slider.",
        "multilingual": false,
    }, 
    // hideTooltip is currently incompatible with oruga slider
    {
        "key": "hideTooltip",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide tooltip",
        "description": "Hide the tooltip that shows up while sliding the slider.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, 
    {
        "key": "minValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "title": "Minimum value",
        "description": "The value of the response when the slider is all the way to the left.",
        "default": 0,
        "multilingual": false,
    }, {    
        "key": "maxValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "title": "Maximum value",
        "description": "The value of the response when the slider is all the way to the right.",
        "default": 100,
        "multilingual": false,
    }, {    
        "key": "defaultValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "title": "Default value",
        "description": "The initial value for the slider when a respondent loads the page.",
        "default": 50,
        "multilingual": false,
    }, {
        "key": "hideDefaultValue",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Hide default value",
        "description": "Select this to hide the default value when no response has been given.",
        "multilingual": false,
    }, {    
        "key": "stepValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "basic",
        "title": "Step size",
        "description": "The step size for the slider when the respondent is moving it.",
        "default": 1,
        "multilingual": false,
    }],
});

export const range = new QuestionnaireElementType({
    name: "range",
    isQuestion: true,
    allowedInPopup: true,
    isRangeResponse: "number",
    icon: ["fal", "sliders-h"],
    title: "Range",
    description: "Range question",
    responseContentType: "list.number",
    labelPropertyName: "label",
    minValuePropertyName: "minValue",
    maxValuePropertyName: "maxValue",
    hiddenFromInterface: true,
    isDeprecated: true,
    tabGroups: [basicTabGroup, extraTabGroup],
    getResponse: function (response) {
        let parsedValue;
        try {
            parsedValue = JSON.parse(response);
        } catch(e) {
            parsedValue = Array.isArray(response) ? response : [];
        }
        return parsedValue.map((n: any) => (Number.isFinite(+n) ? +n : undefined));
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the slider.",
        "multilingual": true,
    }, {    
        "key": "minLabel",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "title": "Minimum label",
        "description": "The label for the minimum end of the slider",
        "default": null,
        "multilingual": true,
    }, {    
        "key": "maxLabel",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "title": "Maximum label",
        "description": "The label for the maximum end of the slider",
        "default": null,
        "multilingual": true,
    }, {
        "key": "labelPosition",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["above", "beside", "below"],
        "valueOptionTexts": ["Above", "Beside", "Below"],
        "group": "style",
        "tabGroup": "extra",
        "default": "beside",
        "title": "Label position",
        "description": "Where to place the labels relative to the slider.",
        "multilingual": false,
    }, {
        "key": "showValue",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Show value",
        "description": "Show the current value of the slider.",
        "multilingual": false,
    }, 
    // hideTooltip is currently incompatible with oruga slider
    {
        "key": "hideTooltip",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide tooltip",
        "description": "Hide the tooltip that shows up while sliding the slider.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, 
    //
    {
        "key": "minValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "extra",
        "title": "Minimum value",
        "description": "The value of the response when the slider is all the way to the left.",
        "default": 0,
        "multilingual": false,
    }, {    
        "key": "maxValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "extra",
        "title": "Maximum value",
        "description": "The value of the response when the slider is all the way to the right.",
        "default": 100,
        "multilingual": false,
    }, {    
        "key": "defaultStart",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "extra",
        "title": "Default start",
        "description": "The initial value for the beginning of the range when a respondent loads the page.",
        "default": 30,
        "multilingual": false,
    }, {    
        "key": "defaultEnd",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "extra",
        "title": "Default end",
        "description": "The initial value for the end of the range when a respondent loads the page.",
        "default": 70,
        "multilingual": false,
    }, {
        "key": "hideDefaultValue",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide default value",
        "description": "Select this to hide the default value when no response has been given.",
        "multilingual": false,
    }, {    
        "key": "stepValue",
        "type": "number",
        "inputType": "number",
        "group": "functional",
        "tabGroup": "extra",
        "title": "Step size",
        "description": "The step size for the slider when the respondent is moving it.",
        "default": 1,
        "multilingual": false,
    }],
});


export const fileUpload = new QuestionnaireElementType({
    name: "fileUpload",
    isQuestion: true,
    sortIndex: 10,
    allowedInPopup: true,
    isMultiQuestion: true,
    icon: ["fal", "arrow-alt-up"],
    title: "File upload",
    description: "Attach files to the question",
    responseContentType: "fileUploadUrl",
    labelPropertyName: "label",
    maximumResponsesPropertyName: "maximumResponses",
    tabGroups: [basicTabGroup, extraTabGroup],
    // TODO: The server should enforce this specification
    responseContextSpecification: {
        "type": "object",
        "attributes": {
            "fileName": {"type": "string"},
            "fileSizeBytes": {"type": "number"},
            "fileMimeType": {"type": "string"},
        },
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the file upload.",
        "multilingual": true,
    }, 
    ...ButtonProperties, 
    {
        // DEPRECIATED
        // DEPRECIATED
        "key": "text",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Question text",
        "description": "The text to show for the upload question.",
        "multilingual": true,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }, {
        "key": "accept",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["any", "image", "video", "audio", "document"],
        "valueOptionTexts": ["Any", "Image", "Video", "Audio", "Document"],
        "group": "functional",
        "tabGroup": "extra",
        "default": "any",
        "title": "File types",
        "description": "Restrict the file types that can be uploaded.",
        "multilingual": false,
    }, {
        "key": "maximumFileSize",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": 5,
        "min": 1,
        "max": 100,
        "title": "Maximum file size (MB)",
        "description": "The maximum allowed size for each file in megabytes (MB).",
        "multilingual": false,
    }, {
        "key": "required",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Required",
        "description": "The respondent must upload a file before proceeding.",
        "multilingual": false,
    }, {
        "key": "multiple",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Multiple uploads",
        "description": "Allow upload of multiple files.",
        "multilingual": false,
        "notAllowedInPopups": true,
    }, {
        "key": "minimumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "min": 0,
        "max": 20,
        "default": 0,
        "title": "Minimum number of uploads",
        "description": "Minimum number of uploads",
        "multilingual": false,
        "notAllowedInPopups": true,
    }, {
        "key": "maximumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "min": 0,
        "max": 20,
        "default": null,
        "title": "Maximum number of uploads",
        "description": "Maximum number of uploads",
        "multilingual": false,
        "notAllowedInPopups": true,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "textColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "basic",
        "default": "#363636",
        "title": "Text color",
        "description": "The color to use for the upload button's text and icon. Use the color selector or specify a hex value.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }],
});

export const fileDownload = new QuestionnaireElementType({
    name: "fileDownload",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fal", "arrow-alt-down"],
    title: "file download",
    description: "file download",
    labelPropertyName: null,
    tabGroups: [basicTabGroup],
    propertiesList: [{
        "key": "assetId",
        "type": "resourceId",
        "isResourceId": "asset",
        "inputType": "documentAssetId",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "asset",
        "description": "The file to be downloaded.",
        "multilingual": true,
    }, {
        "key": "fileName",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "File Name",
        "description": "The name of the file when it is downloaded.",
        "multilingual": true,
    }, 
    ...ButtonProperties],
});


/** ******************************************************************
 *
 * GEO elements
 *
 ********************************************************************/
export const geoPoint = new QuestionnaireElementType({
    name: "geoPoint",
    isQuestion: true,
    allowedInPopup: false,
    isMultiQuestion: true,
    isGeoQuestion: true,
    hasPopup: true,
    icon: ["fal", "map-marker"],
    title: "Geo point",
    description: "Place a point on the map",
    responseContentType: "latlon",
    labelPropertyName: "label",
    maximumResponsesPropertyName: "maximumResponses",
    identifyingTextPropertyNames: [
        "buttonText",
        "label",
        "layerControlText",
    ],
    tabGroups: [basicTabGroup, extraTabGroup, budgetingTabGroup],
    getWellKnownText: (content, contentType) => {
        if(contentType === "latlon") {
            const wktData = parseGeoResponseContentPointLonLat(content);
            return new Wkt(Wkt.DataType.Point, wktData).toString();
        }
        else {
            return "";
        }
    },
    getGeoJsonGeometry: (content, contentType) => {
        if(contentType === "latlon") {
            const point = parseGeoResponseContentPointLonLat(content);
            return getGeoJsonPointGeometry(point);
        }
        else {
            return {};
        }
    },
    getLatLon: (content, contentType) => {
        if(contentType === "latlon") {
            const point = parseGeoResponseContentPointLonLat(content);
            return {lon: point[0], lat: point[1]};
        }
        else {
            return {lon: undefined, lat: undefined};
        }
    },
    propertiesList: [{
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, 
        ...ButtonProperties, 
    {
        "key": "popupId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "group": "hidden",
        "tabGroup": "basic",
        "default": null,
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "layerControlText",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Layer control text",
        "description": "The text to display in the map's layer menu. If empty, the question's label or drawbutton text will be used.",
        "multilingual": true,
        "hiddenFromInterface": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "hideFromLayerControl",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide from layer menu",
        "description": "Whether to hide the layer from the map's layer menu.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "minimumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "min": 0,
        "default": 0,
        "title": "Minimum number of responses",
        "description": "Minimum number of responses",
        "multilingual": false,
    }, {
        "key": "maximumResponses",
        "min": 1,
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum number of responses",
        "description": "Maximum number of responses",
        "multilingual": false,
    }, {
        "key": "showResponsesByOthers",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Show responses by others",
        "description": "Show responses to this question given by other respondents.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, 
    ...GeoProperties, ParticipatoryIdeaProperty("geoPoint"),
    {
        "key": "layerAssetIds",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "group": "content",
        "tabGroup": "extra",
        "assetGroup": "maps",
        "inputType": "assetPickerMany",
        "inputAssetTypes": ["geojson", "shapefile", "wfsmap"],
        "default": [],
        "title": "Mask layers",
        "description": "Select layers to restricts where respondents can place points. The layers needs to be available on this page of the questionnaire.",
        "multilingual": false,
    }, 
    {
        "key": "insideMask",
        "type": "boolean",
        "inputType": "ownOption",
        "valueOptions": [true, false],
        "valueOptionTexts": ["Inside area", "Outside area"],
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Inside Mask",
        "description": "Features are restricted to the inside of the mask layer",
        "multilingual": false,
    }, 
        ...PublicQuestionElementProperties, 
    {
        "key": "helpText",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Help text",
        "description": "Text to show while the response is being placed on the map.",
        "multilingual": true,
    }, {
        "key": "markerColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": null,
        "title": "Marker color",
        "description": "A separate color to use for the marker that is placed on the map. If this is not set, the 'Drawbutton color' will be used. Use the color selector or specify a hex value.",
        "multilingual": false,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "textColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": "#363636",
        "title": "Text color",
        "description": "The color to use for the drawbutton's text and icon. Use the color selector or specify a hex value.",
        "multilingual": false,
        "hiddenFromInterface": true,
        "isDeprecated": true,
        // DEPRECIATED
        // DEPRECIATED
    }],
});

export const geoLine = new QuestionnaireElementType({
    name: "geoLine",
    isQuestion: true,
    allowedInPopup: false,
    isMultiQuestion: true,
    isGeoQuestion: true,
    hasPopup: true,
    icon: ["fal", "map-marker-minus"],
    title: "Geo line",
    description: "Draw a line on the map",
    responseContentType: "list.latlon",
    labelPropertyName: "label",
    maximumResponsesPropertyName: "maximumResponses",
    identifyingTextPropertyNames: [
        "buttonText",
        "label",
        "layerControlText",
    ],
    tabGroups: [basicTabGroup, extraTabGroup],
    getWellKnownText: (content, contentType) => {
        if(contentType === "list.latlon") {
            const wktData = parseGeoResponseContentPointListLonLat(content);
            return new Wkt(Wkt.DataType.LineString, wktData).toString();
        }
        else {
            return "";
        }
    },
    getGeoJsonGeometry: (content, contentType) => {
        if(contentType === "list.latlon") {
            const points = parseGeoResponseContentPointListLonLat(content);
            return getGeoJsonLineStringGeometry(points);
        }
        else {
            return {};
        }
    },
    propertiesList: [{
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, 
        ...ButtonProperties, 
    {
        "key": "popupId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "group": "hidden",
        "tabGroup": "basic",
        "default": null,
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "layerControlText",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Layer control text",
        "description": "The text to display in the map's layer menu. If empty, the question's label or drawbutton text will be used.",
        "multilingual": true,
        "hiddenFromInterface": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "hideFromLayerControl",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide from layer menu",
        "description": "Whether to hide the layer from the map's layer menu.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "minimumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "min": 0,
        "default": 0,
        "title": "Minimum number of responses",
        "description": "Minimum number of responses",
        "multilingual": false,
    }, {
        "key": "maximumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum number of responses",
        "description": "Maximum number of responses",
        "multilingual": false,
    }, {
        "key": "showResponsesByOthers",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Show responses by others",
        "description": "Show responses to this question given by other respondents.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, 
    ...GeoProperties,
    {
        "key": "layerAssetIds",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "group": "content",
        "tabGroup": "extra",
        "assetGroup": "maps",
        "inputType": "assetPickerMany",
        "inputAssetTypes": ["geojson", "shapefile", "wfsmap"],
        "default": [],
        "title": "Mask layers",
        "description": "Select layers to restricts where respondents can place points. The layers needs to be available on this page of the questionnaire.",
        "multilingual": false,
    }, {
        "key": "insideMask",
        "type": "boolean",
        "inputType": "ownOption",
        "valueOptions": [true, false],
        "valueOptionTexts": ["Inside area", "Outside area"],
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Inside Mask",
        "description": "Features are restricted to the inside of the mask layer",
        "multilingual": false,
    },
        ...PublicQuestionElementProperties, 
    {
        "key": "helpText",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Help text",
        "description": "Text to show while the response is being placed on the map.",
        "multilingual": true,
    }, {
        "key": "strokeColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": null,
        "title": "Line color",
        "description": "A separate color to use for the marking that is drawn on the map. If this is not set, the 'Drawbutton color' will be used. Use the color selector or specify a hex value.",
        "multilingual": false,
    }, {
        "key": "strokeWidth",
        "type": "number",
        "inputType": "slider",
        "group": "style",
        "tabGroup": "extra",
        "default": 2,
        "limits": [1, 15],
        "step": 1,
        "title": "Line width",
        "description": "The width of the line to be set",
        "multilingual": false,
    }, {
        "key": "freehand",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Freehand drawing",
        "description": "Freehand drawing allows the respondent to trace out the response.",
        "multilingual": false,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "textColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": "#363636",
        "title": "Text color",
        "description": "The color to use for the drawbutton's text and icon. Use the color selector or specify a hex value.",
        "multilingual": false,
        "hiddenFromInterface": true,
        // DEPRECIATED
        // DEPRECIATED
    }],
});

export const geoArea = new QuestionnaireElementType({
    name: "geoArea",
    isQuestion: true,
    allowedInPopup: false,
    isMultiQuestion: true,
    isGeoQuestion: true,
    hasPopup: true,
    icon: ["fal", "map-marker-alt"],
    title: "Geo area",
    description: "Draw a region on the map",
    responseContentType: "list.list.latlon",
    labelPropertyName: "label",
    maximumResponsesPropertyName: "maximumResponses",
    identifyingTextPropertyNames: [
        "buttonText",
        "label",
        "layerControlText",
    ],
    tabGroups: [basicTabGroup, extraTabGroup],
    getWellKnownText: (content, contentType) => {
        if(contentType === "list.list.latlon") {
            const wktData = parseGeoResponseContentPointListListLonLat(content);
            return new Wkt(Wkt.DataType.Polygon, wktData).toString();
        }
        else {
            return "";
        }
    },
    getGeoJsonGeometry: (content, contentType) => {
        if(contentType === "list.list.latlon") {
            const polygon = parseGeoResponseContentPointListListLonLat(content);
            return getGeoJsonPolygonGeometry(polygon);
        }
        else {
            return {};
        }
    },
    propertiesList: [{
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, 
        ...ButtonProperties, 
    {
        "key": "popupId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "group": "hidden",
        "tabGroup": "basic",
        "default": null,
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "layerControlText",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Layer control text",
        "description": "The text to display in the map's layer menu. If empty, the question's label or drawbutton text will be used.",
        "multilingual": true,
        "hiddenFromInterface": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "hideFromLayerControl",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide from layer menu",
        "description": "Whether to hide the layer from the map's layer menu.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "minimumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "min": 0,
        "default": 0,
        "title": "Minimum number of responses",
        "description": "Minimum number of responses",
        "multilingual": false,
    }, {
        "key": "maximumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum number of responses",
        "description": "Maximum number of responses",
        "multilingual": false,
    }, {
        "key": "showResponsesByOthers",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Show responses by others",
        "description": "Show responses to this question given by other respondents.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, 
    ...GeoProperties,
    {
        "key": "layerAssetIds",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "group": "content",
        "tabGroup": "extra",
        "assetGroup": "maps",
        "inputType": "assetPickerMany",
        "inputAssetTypes": ["geojson", "shapefile", "wfsmap"],
        "default": [],
        "title": "Mask layers",
        "description": "Select layers to restricts where respondents can place points. The layers needs to be available on this page of the questionnaire.",
        "multilingual": false,
    }, {
        "key": "insideMask",
        "type": "boolean",
        "inputType": "ownOption",
        "valueOptions": [true, false],
        "valueOptionTexts": ["Inside area", "Outside area"],
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Inside Mask",
        "description": "Features are restricted to the inside of the mask layer",
        "multilingual": false,
    }, 
        ...PublicQuestionElementProperties, 
    {
        "key": "helpText",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Help text",
        "description": "Text to show while the response is being placed on the map.",
        "multilingual": true,
    }, {
        "key": "strokeColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": null,
        "title": "Area border color",
        "description": "A separate color to use for the marking that is drawn on the map. If this is not set, the 'Drawbutton color' will be used. Use the color selector or specify a hex value.",
        "multilingual": false,
    }, {
        "key": "strokeWidth",
        "type": "number",
        "inputType": "slider",
        "group": "style",
        "tabGroup": "extra",
        "default": 2,
        "limits": [1, 15],
        "step": 1,
        "title": "Line width",
        "description": "The width of the line to be set",
        "multilingual": false,
    }, {
        "key": "fillColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": null,
        "title": "Area fill color",
        "description": "A separate color to use for the marking that is drawn on the map. If this is not set, the 'Drawbutton color' will be used. Use the color selector or specify a hex value.",
        "multilingual": false,
    }, {
        "key": "freehand",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Freehand drawing",
        "description": "Freehand drawing allows the respondent to trace out the response.",
        "multilingual": false,
    }, {
        // DEPRECIATED
        // DEPRECIATED
        "key": "textColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": "#363636",
        "title": "Text color",
        "description": "The color to use for the drawbutton's text and icon. Use the color selector or specify a hex value.",
        "multilingual": false,
        "hiddenFromInterface": true,
        // DEPRECIATED
        // DEPRECIATED
    }],
});


export const geoBudgetingPoints = new QuestionnaireElementType({
    name: "geoBudgetingPoints",
    isQuestion: true,
    allowedInPopup: false,
    needsLicense: "feature:questionnaire:budgeting",
    isMultiQuestion: false,
    isGeoQuestion: true,
    icon: ["custom", "geoBudgetingPoints"],
    title: "Geo budgeting",
    description: "Place points on the map while keeping within the allotted budget.",
    responseContentType: "list.list.latlon",
    labelPropertyName: "label",
    optionsPropertyName: "buttonNames",
    optionDataPropertyNames: {
        "label": "buttons",
        "cost": "buttonCosts",
    },
    parallelGroups: [{
        group: "buttonGroup",
        title: "Buttons",
        labelPropertyName: "buttons",
        description: "Buttons that let you place responses on the map.",
        tabGroups: [basicTabGroup, extraTabGroup, styleTabGroup],
        tabGroup: "basic",
    }],
    tabGroups: [basicTabGroup, extraTabGroup],
    getWellKnownText: (content, contentType) => {
        if(contentType === "latlon") {
            const wktPointsData = parseGeoResponseContentPointListListLonLat(content);
            const wktCollectionData = wktPointsData.map(
                (points) => (new Wkt(Wkt.DataType.MultiPoint, points))
            );
            return new Wkt(Wkt.DataType.GeometryCollection, wktCollectionData).toString();
        }
        else {
            return "";
        }
    },
    getGeoJsonGeometry: (content, contentType) => {
        if(contentType === "list.list.latlon") {
            const points = parseGeoResponseContentPointListListLonLat(content);
            return getGeoJsonCollectionGeometry(
                points.map(getGeoJsonMultiPointGeometry)
            );
        }
        else {
            return {};
        }
    },
    propertiesList: [{
        "key": "publicResponses",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Make responses public",
        "description": "Responses are publicly shared and visible among respondents",
        "multilingual": false,
        "hiddenFromInterface": true,
        "needsLicense": "feature:questionnaire:response-reactions",
    }, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, {
        "key": "description",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Description",
        "description": "A description for this question.",
        "multilingual": true,
    }, {
        "key": "budget",
        "type": "number",
        "inputType": "number",
        "tabGroup": "basic",
        "group": "content",
        "default": null,
        "title": "Budget",
        "description": "The total amount of available funds.",
        "multilingual": false,
    }, {
        "key": "unit",
        "type": "string",
        "inputType": "text",
        "tabGroup": "basic",
        "group": "content",
        "default": null,
        "title": "Unit",
        "description": "The unit of the funds.",
        "multilingual": true,
    }, {
        "key": "unitFirst",
        "type": "boolean",
        "inputType": "switch",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Unit first",
        "description": "Place the unit symbol before the number.",
        "multilingual": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "layerControlText",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Layer control text",
        "description": "The text to display in the map's layer menu. If empty, the question's label will be used.",
        "multilingual": true,
        "hiddenFromInterface": true,
    }, {
        // not used, remaining here for usage in existing questionnaires
        "key": "hideFromLayerControl",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide from layer menu",
        "description": "Whether to hide the layer from the map's layer menu.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "helpText",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Help text",
        "description": "Text to show while the response is being placed on the map.",
        "multilingual": true,
    }, {
        "key": "hideProgressbar",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Hide progress bar",
        "description": "Hide budget limit as a progress bar.",
        "multilingual": false,
    }, {
        "key": "hideItemCosts",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "content",
        "default": false,
        "title": "Hide item costs",
        "description": "Hide the cost for each item.",
        "multilingual": false,
    }, {
        "key": "randomize",
        "type": "boolean",
        "inputType": "checkbox",
        "tabGroup": "extra",
        "group": "functional",
        "default": false,
        "title": "Randomize",
        "description": "Randomize the order of the items for each respondent.",
        "multilingual": false,
    }, {
        "key": "showResponsesByOthers",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Show responses by others",
        "description": "Show responses to this question given by other respondents.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, 
    ...GeoProperties,
    {
        "key": "buttons",
        "type": "list.string",
        "inputType": "text",
        "group": "buttonGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Drawbutton text",
        "description": "The text to display on the drawbutton.",
        "multilingual": true,
    }, {
        "key": "buttonCosts",
        "type": "list.number",
        "inputType": "number",
        "tabGroup": "basic",
        "group": "buttonGroup",
        "default": [],
        "defaultInsert": "",
        "title": "Button cost",
        "description": "The cost of selecting this button.",
        "multilingual": false,
    }, {
        "key": "buttonNames",
        "type": "list.string",
        "inputType": "text",
        "group": "buttonGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Sub-question identifier",
        "description": "The data name identifying this question.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }, {
        "key": "buttonLabels",
        "type": "list.string",
        "inputType": "text",
        "group": "buttonGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Label",
        "description": "A separate question label for this button.",
        "multilingual": true,
    }, {
        "key": "buttonDescriptions",
        "type": "list.string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "buttonGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Description",
        "description": "A separate description for the button.",
        "multilingual": true,
    }, {
        "key": "buttonColor",
        "type": "list.string",
        "inputType": "color",
        "group": "buttonGroup",
        "tabGroup": "style",
        "default": [],
        "defaultInsert": "",
        "title": "Drawbutton color",
        "description": "The color to use for the drawbutton. Use the color selector or specify a hex value.",
        "multilingual": false,
        "autoFill": "palette",
    }, {
        "key": "darkTextColor",
        "type": "list.boolean",
        "group": "buttonGroup",
        "tabGroup": "style",
        "inputType": "switch",
        "default": [],
        "defaultInsert": false,
        "title": "Dark text color",
        "description": "Use a dark text color for the button text.",
        "multilingual": false,
    }, {
        "key": "invertColors",
        "type": "list.boolean",
        "group": "buttonGroup",
        "tabGroup": "style",
        "inputType": "switch",
        "default": [],
        "defaultInsert": false,
        "title": "Invert colors",
        "description": "Switch the button color and the text color.",
        "multilingual": false,
    }, {
        "key": "icon",
        "type": "list.list.string",
        "inputType": "iconOption",
        "group": "buttonGroup",
        "tabGroup": "style",
        "default": [],
        "defaultInsert": [],
        "title": "Icon",
        "description": "Icon to show in the button and marker. See <a href='https://fontawesome.com/icons' target='_blank'>Font Awesome</a> for supported icons.",
        "multilingual": false,
    }, {
        "key": "iconClass",
        "type": "list.string",
        "inputType": "iconClassOption",
        "group": "buttonGroup",
        "tabGroup": "style",
        "default": [],
        "defaultInsert": "fal",
        "title": "Icon weight",
        "description": "The weight (light, regular, bold) to use for the icon.",
        "multilingual": false,
    }, 
    // deprecated? markerColor doesn't seem to be in the UI for geoBudgetingPoints anymore
    {
        "key": "markerColor",
        "type": "list.string",
        "inputType": "color",
        "group": "buttonGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Marker color",
        "description": "A separate color to use for the marker that is placed on the map. If this is not set, the 'Drawbutton color' will be used. Use the color selector or specify a hex value.",
        "multilingual": false,
    },
    {
        "key": "layerAssetIds",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "group": "content",
        "tabGroup": "extra",
        "assetGroup": "maps",
        "inputType": "assetPickerMany",
        "inputAssetTypes": ["geojson", "shapefile", "wfsmap"],
        "default": [],
        "title": "Mask layers",
        "description": "Select layers to restricts where respondents can place points. The layers needs to be available on this page of the questionnaire.",
        "multilingual": false,
    },
    {
        "key": "insideMask",
        "type": "boolean",
        "inputType": "ownOption",
        "valueOptions": [true, false],
        "valueOptionTexts": ["Inside area", "Outside area"],
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Inside Mask",
        "description": "Features are restricted to the inside of the mask layer",
        "multilingual": false,
    }],
});

export const clickLayer = new QuestionnaireElementType({
    name: "clickLayer",
    isQuestion: true,
    allowedInPopup: false,
    isMultiQuestion: true,
    isGeoQuestion: true,
    hasPopup: true,
    abusesResponseNumber: true,
    icon: ["fal", "map"],
    title: "Click layer",
    description: "Click on the features of a vector layer.",
    responseContentType: "string", // geojson of selected feature
    labelPropertyName: "label",
    tabGroups: [basicTabGroup, extraTabGroup],
    getWellKnownText: (content, contentType) => {
        if (contentType !== "string") return "";
        let json, wktData;
        try {
            json = JSON.parse(content);
        } catch (error) {
            // TODO should not happen, SENTRY this
            return "";
        }
        switch (json.geometry.type) {
        case "MultiPoint":
            return new Wkt(Wkt.DataType.MultiPoint, json.geometry.coordinates).toString();
        case "Point":
            return new Wkt(Wkt.DataType.Point, json.geometry.coordinates).toString();
        case "LineString":
            return new Wkt(Wkt.DataType.LineString, json.geometry.coordinates).toString();
        case "MultiLineString":
            return new Wkt(Wkt.DataType.MultiLineString, json.geometry.coordinates).toString();
        case "Polygon":
            return new Wkt(Wkt.DataType.Polygon, json.geometry.coordinates).toString();
        case "MultiPolygon":
            return new Wkt(Wkt.DataType.MultiPolygon, json.geometry.coordinates).toString();
        default:
            return "";
        }
    },
    getGeoJsonGeometry: (content, contentType) => {
        if (contentType !== "string") return {};
        let json, geom;
        try {
            json = JSON.parse(content);
        } catch (error) {
            // TODO should not happen, SENTRY this
            return {};
        }
        switch (json.geometry.type) {
        case "MultiPoint":
            return getGeoJsonMultiPointGeometry(json.geometry.coordinates);
        case "Point":
            return getGeoJsonPointGeometry(json.geometry.coordinates);
        case "MultiLineString":
            return getGeoJsonMultiLineStringGeometry(json.geometry.coordinates);
        case "LineString":
            return getGeoJsonLineStringGeometry(json.geometry.coordinates);
        case "Polygon":
            return getGeoJsonPolygonGeometry(json.geometry.coordinates);
        case "MultiPolygon":
            return getGeoJsonMultiPolygonGeometry(json.geometry.coordinates);
        default:
            return {};
        }
    },
    propertiesList: [...PublicQuestionElementProperties, {
        "key": "layerAssetId",
        "type": "resourceId",
        "isResourceId": "asset",
        "inputType": "mapAssetId",
        "assetGroup": "maps",
        "inputAssetTypes": ["geojson", "shapefile", "wfsmap", "geotiff"],
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Map asset",
        "description": "The map layer that respondents should be able to click. The layer needs to be available on this page of the questionnaire. You can manage the availability of layers in the 'Maps' tab of the questionnaire settings. ",
        "multilingual": false,
    }, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, {
        "key": "text",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question text",
        "description": "The text to show for the question.",
        "multilingual": true,
    }, 
    ...ButtonProperties, 
    {
        "key": "alwaysRespond",
        "type": "boolean",
        "inputType": "switch",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Always respond",
        "description": "By default, if there is no pop-up, or if there are no questions in the pop-up, no response is generated when a feature is clicked. Enable this setting if you want to record a response each time a feature is clicked.",
        "multilingual": false,
        "hiddenFromInterface": true,
        // TODO: this is currently hidden b/c there is no good way yet for showing if a 
        // clickable map layer has been clicked or not.
    }, {
        "key": "hideButton",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide button",
        "description": "Hide the button that shows the full map.",
        "multilingual": false,
    }, {
        "key": "hideElement",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide element",
        "description": "Hide the entire element from view.",
        "multilingual": false,
    }, {
        "key": "hideFromLayerControl",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": true,
        "title": "Hide from layer menu",
        "description": "This is a default setting that should not be changed.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "fitViewOnShow",
        "type": "boolean",
        "inputType": "switch",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Zoom to layer",
        "description": "Enable this setting, if you want to zoom to the selected layer in the map window when the button is clicked.",
        "multilingual": false,
    }, {
        "key": "fitMaximumZoom",
        "type": "number",
        "inputType": "olZoom",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum zoom",
        "description": "Use this setting if the \"Zoom to layer\" action zooms in too much. This can be the case for isolated small features. Adjust the map to a suitable zoom level and use the \"Get map zoom\" button to set this value.",
        "multilingual": false,
    }, {
        "key": "minimumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "min": 0,
        "default": 0,
        "title": "Minimum number of responses",
        "description": "Minimum number of responses",
        "multilingual": false,
        // TODO: this is currently hidden b/c there is no good way yet for showing if a 
        // clickable map layer has been clicked or not.
        "hiddenFromInterface": true,
    }, {
        "key": "maximumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum number of responses",
        "description": "Maximum number of responses",
        "multilingual": false,
        // TODO: this is currently hidden b/c there is no good way yet for showing if a 
        // clickable map layer has been clicked or not.
        "hiddenFromInterface": true,
    }, {
        "key": "popupId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "group": "hidden",
        "tabGroup": "basic",
        "default": null,
        "multilingual": false,
        "hiddenFromInterface": true,
    }],
});

export const selectLayer = new QuestionnaireElementType({
    name: "selectLayer",
    isQuestion: true,
    allowedInPopup: false,
    isMultiQuestion: true,
    isGeoQuestion: true,
    hasPopup: false,
    abusesResponseNumber: true,
    icon: ["fas", "map"],
    title: "Select layer",
    description: "Click on the features of a vector layer to select them.",
    responseContentType: "string",
    labelPropertyName: "label",
    tabGroups: [basicTabGroup, extraTabGroup],
    getWellKnownText: (content, contentType) => {
        if (contentType !== "string") return "";
        let json, wktData;
        try {
            json = JSON.parse(content);
        } catch (error) {
            return "";
        }
        switch (json.geometry.type) {
        case "MultiPoint":
            return new Wkt(Wkt.DataType.MultiPoint, json.geometry.coordinates).toString();
        case "Point":
            return new Wkt(Wkt.DataType.Point, json.geometry.coordinates).toString();
        case "LineString":
            return new Wkt(Wkt.DataType.LineString, json.geometry.coordinates).toString();
        case "MultiLineString":
            return new Wkt(Wkt.DataType.MultiLineString, json.geometry.coordinates).toString();
        case "Polygon":
            return new Wkt(Wkt.DataType.Polygon, json.geometry.coordinates).toString();
        case "MultiPolygon":
            return new Wkt(Wkt.DataType.MultiPolygon, json.geometry.coordinates).toString();
        default:
            return "";
        }
    },
    getGeoJsonGeometry: (content, contentType) => {
        if (contentType !== "string") return {};
        let json, geom;
        try {
            json = JSON.parse(content);
        } catch (error) {
            return {};
        }
        switch (json.geometry.type) {
        case "MultiPoint":
            return getGeoJsonMultiPointGeometry(json.geometry.coordinates);
        case "Point":
            return getGeoJsonPointGeometry(json.geometry.coordinates);
        case "MultiLineString":
            return getGeoJsonMultiLineStringGeometry(json.geometry.coordinates);
        case "LineString":
            return getGeoJsonLineStringGeometry(json.geometry.coordinates);
        case "MultiPolygon":
            return getGeoJsonMultiPolygonGeometry(json.geometry.coordinates);
        case "Polygon":
            return getGeoJsonPolygonGeometry(json.geometry.coordinates);
        default:
            return {};
        }
    },
    propertiesList: [
    // There are no public responses properties unless/until we can think of a good way to display them
    // ...PublicQuestionElementProperties, 
    {
        "key": "layerAssetId",
        "type": "resourceId",
        "isResourceId": "asset",
        "inputType": "mapAssetId",
        "assetGroup": "maps",
        "inputAssetTypes": ["geojson", "shapefile", "wfsmap", "geotiff"],
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Map asset",
        "description": "The map layer that respondents should be able to click. The layer needs to be available on this page of the questionnaire. You can manage the availability of layers in the 'Maps' tab of the questionnaire settings. ",
        "multilingual": false,
    }, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, {
        "key": "text",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question text",
        "description": "The text to show for the question.",
        "multilingual": true,
    }, {
        "key": "minRequired",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "basic",
        "min": 0,
        "default": null,
        "title": "Minimum required",
        "description": "The respondent must select at least this many features before proceeding.",
        "multilingual": false,
    }, {
        "key": "maxAllowed",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "basic",
        "default": null,
        "title": "Maximum allowed",
        "description": "The respondent may select at most this many features before proceeding.",
        "multilingual": false,
    },
    ...ButtonProperties, 
    {
        "key": "hideButton",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide button",
        "description": "Hide the button that shows the full map.",
        "multilingual": false,
    }, {
        "key": "hideElement",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide element",
        "description": "Hide the entire element from view.",
        "multilingual": false,
    }, {
        "key": "hideFromLayerControl",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": true,
        "title": "Hide from layer menu",
        "description": "This is a default setting that should not be changed.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "fitViewOnShow",
        "type": "boolean",
        "inputType": "switch",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Zoom to layer",
        "description": "Enable this setting, if you want to zoom to the selected layer in the map window when the button is clicked.",
        "multilingual": false,
    }, {
        "key": "fitMaximumZoom",
        "type": "number",
        "inputType": "olZoom",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum zoom",
        "description": "Use this setting if the \"Zoom to layer\" action zooms in too much. This can be the case for isolated small features. Adjust the map to a suitable zoom level and use the \"Get map zoom\" button to set this value.",
        "multilingual": false,
    }],
});

// sova3dPoint is removed from element lib but leaving this here so existing usage doesn't break
export const sova3dPoint = new QuestionnaireElementType({
    name: "sova3dPoint",
    needsLicense: "feature:questionnaire:3d",
    isQuestion: true,
    allowedInPopup: false,
    isMultiQuestion: true,
    isGeoQuestion: true,
    hasPopup: true,
    icon: ["fal", "map-marker"],
    title: "Sova3D point",
    description: "Place a point in a Sova3D map",
    responseContentType: "string", // {lat, lon} JSON object
    labelPropertyName: "label",
    maximumResponsesPropertyName: "maximumResponses",
    tabGroups: [basicTabGroup, extraTabGroup, responseTabGroup],
    getWellKnownText: (content, contentType) => {
        if(contentType !== "string") return "";
        let json;
        try {
            json = JSON.parse(content);
        } catch (error) {
            // TODO should not happen, SENTRY this
            return "";
        }
        const wktData: [number, number] = [+json.lon || 0, +json.lat || 0];
        return new Wkt(Wkt.DataType.Point, wktData).toString();
    },
    getGeoJsonGeometry: (content, contentType) => {
        if(contentType !== "string") return {};
        let json;
        try {
            json = JSON.parse(content);
        } catch (error) {
            // TODO should not happen, SENTRY this
            return {};
        }
        const geom: [number, number] = [+json.lon || 0, +json.lat || 0];
        return getGeoJsonPointGeometry(geom);
    },
    propertiesList: [
        ...PublicQuestionElementProperties, 
        ...ButtonProperties, 
    {
        "key": "popupId",
        "type": "resourceId",
        "isResourceId": "questionnaireElementGroup",
        "group": "hidden",
        "tabGroup": "basic",
        "default": null,
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Question label",
        "description": "The label for this question.",
        "multilingual": true,
    }, {
        "key": "layerControlText",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Layer control text",
        "description": "The text to display in the map's layer menu. If empty, the question's label or drawbutton text will be used.",
        "multilingual": true,
        "hiddenFromInterface": true,
    }, {
        "key": "hideFromLayerControl",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide from layer menu",
        "description": "Whether to hide the layer from the map's layer menu.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }, {
        "key": "helpText",
        "type": "string",
        "inputType": "textarea",
        "native.rows": 4,
        "group": "content",
        "tabGroup": "extra",
        "default": null,
        "title": "Help text",
        "description": "Text to show while the response is being placed on the map.",
        "multilingual": true,
    }, {
        "key": "markerColor",
        "type": "string",
        "inputType": "color",
        "group": "style",
        "tabGroup": "extra",
        "default": null,
        "title": "Marker color",
        "description": "A separate color to use for the marker that is placed on the map. If this is not set, the 'Drawbutton color' will be used. Use the color selector or specify a hex value.",
        "multilingual": false,
    }, {
        "key": "minimumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "min": 0,
        "default": 0,
        "title": "Minimum number of responses",
        "description": "Minimum number of responses",
        "multilingual": false,
    }, {
        "key": "maximumResponses",
        "type": "number",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "extra",
        "default": null,
        "title": "Maximum number of responses",
        "description": "Maximum number of responses",
        "multilingual": false,
    }, {
        "key": "showResponsesByOthers",
        "type": "boolean",
        "inputType": "switch",
        "group": "content",
        "tabGroup": "extra",
        "default": false,
        "title": "Show responses by others",
        "description": "Show responses to this question given by other respondents.",
        "multilingual": false,
        "hiddenFromInterface": true,
    }],
});

// discussion element is removed from element lib but leaving this here so existing usage doesn't break
export const discussion = new QuestionnaireElementType({
    isDeprecated: true,
    name: "discussion",
    isQuestion: false,
    allowedInPopup: true,
    icon: ["fal", "comment-alt-dots"],
    title: "Discussion element",
    description: "Discussion threads that allow commenting on different topics",
    labelPropertyName: "label",
    tabGroups: [basicTabGroup, responseTabGroup],
    propertiesList: [
    {
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Discussion topic",
        "description": "The heading which is shown above the comment submission form of a discussion. When using discussion elements, remember two related questionnaire settings: \"Allow discussion elements\" and \"Require authentication\".",
        "multilingual": true,
    }, {
        "key": "placeholder",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Placeholder text",
        "description": "Text to display when the comment field is empty.",
        "multilingual": true,
    }, {
        "key": "maxLength",
        "type": "integer",
        "inputType": "integer",
        "group": "functional",
        "tabGroup": "basic",
        "default": 1000,
        "title": "Maximum text length",
        "description": "The maximum length of the comment that this input will accept.",
        "unit": "characters",
        "multilingual": false,
    }],
});

export const rank = new QuestionnaireElementType({
    name: "rank",
    isQuestion: true,
    sortIndex: 8,
    allowedInPopup: true,
    allowAggregation: true,
    tabGroups: [basicTabGroup, extraTabGroup],
    parallelGroups: [{
        group: "optionGroup",
        title: "Options",
        description: "Options that can be placed in ranked order.",
        labelPropertyName: "options",
        tabGroups: [basicTabGroup, extraTabGroup],
        tabGroup: "basic",
    }],
    icon: ["fal", "list-ol"],
    title: "Ranked choice",
    description: "A list of options to be ranked.",
    responseContentType: "list.integer",
    labelPropertyName: "label",
    optionsPropertyName: "optionNames",
    optionDataPropertyNames: {
        "label": "options",
    },
    getResponse: function (response) {
        return response;
    },
    propertiesList: [{
        "key": "label",
        "type": "string",
        "inputType": "text",
        "group": "content",
        "tabGroup": "basic",
        "default": null,
        "title": "Question label",
        "description": "The label to show above the list.",
        "multilingual": true,
    }, {
        "key": "required",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "basic",
        "default": false,
        "title": "Required",
        "description": "The respondent must rank all items before proceeding.",
        "multilingual": false,
    }, {
        "key": "style",
        "type": "string",
        "inputType": "ownOption",
        "valueOptions": ["list", "images"],
        "valueOptionTexts": ["List", "Images"],
        "group": "style",
        "tabGroup": "basic",
        "default": "list",
        "title": "List style",
        "description": "How to arrange the layout of the list of options to rank.",
        "multilingual": false,
    }, {
        "key": "hideNames",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "style",
        "tabGroup": "extra",
        "default": false,
        "title": "Hide option names",
        "description": "When the selected list style is Images, this will hide the option names.",
        "multilingual": false,
    }, {
        "key": "randomize",
        "type": "boolean",
        "inputType": "checkbox",
        "group": "functional",
        "tabGroup": "extra",
        "default": false,
        "title": "Randomize",
        "description": "Randomize the order of the options for each respondent.",
        "multilingual": false,
    }, {
        "key": "options",
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "basic",
        "default": [],
        "defaultInsert": "",
        "title": "Option",
        "description": "The label for this option.",
        "multilingual": true,
    }, {
        "key": "optionImages",
        "type": "list.resourceId",
        "isResourceIdList": "asset",
        "inputType": "imageAssetId",
        "group": "optionGroup",
        "tabGroup": "extra",
        "default": [],
        "defaultInsert": "",
        "title": "Option image",
        "description": "The image for this option.",
        "multilingual": true,
        "excludedByDefault": true,
    }, {
        "key": "optionNames", // is this needed?
        "type": "list.string",
        "inputType": "text",
        "group": "optionGroup",
        "tabGroup": "response",
        "default": [],
        "defaultInsert": "",
        "title": "Option identifier",
        "description": "The data name identifying this option.",
        "multilingual": false,
        "autoFill": "uid",
        "isDataName": true,
    }, 
    // DEPRECIATED
    // DEPRECIATED
    {
        "key": "analysisFormatType",
        "type": "string",
        "group": "hidden",
        "tabGroup": "basic",
        "inputType": "ownOption",
        "valueOptions": ["percent", "count"],
        "valueOptionTexts": ["Percentage of Respondents", "Respondent Count"],
        "default": "count",
        "title": "Results format type",
        "description": "The results format to use for displaying the aggregate results.",
        "multilingual": false,
        "hiddenFromInterface": true,
    },
    // DEPRECIATED
    // DEPRECIATED
    ],
});